import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import api from '../../functions/api';
import './AlertDiscovery.css';

const AlertDiscovery = () => {
  const [runs, setRuns] = useState([]);
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [clauseTexts, setClauseTexts] = useState({});
  const [candidates, setCandidates] = useState([]);
  // DAS-2458: text filter for the candidates table — match against ID
  // (substring). Empty string = no filter. Only filters the currently
  // open run's candidates; for cross-run lookup see globalCandidateLookup.
  const [candidateIdSearch, setCandidateIdSearch] = useState('');
  // DAS-2458: cross-run candidate-ID lookup. Hits /candidate/<id>/locate
  // to discover the run_id, then opens that run + highlights the row.
  const [globalCandidateLookup, setGlobalCandidateLookup] = useState('');
  const [locatingCandidate, setLocatingCandidate] = useState(false);
  const [locateError, setLocateError] = useState('');
  // DAS-2458: "All Candidates" cross-run view + sortable columns.
  //   viewMode 'run' = legacy (one run at a time)
  //   viewMode 'all' = all candidates across all runs, sortable
  const [viewMode, setViewMode] = useState('run');
  const [allCandidates, setAllCandidates] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  // sortKey: column to sort by, sortDir: 'asc' | 'desc'. Default = score desc.
  const [sortKey, setSortKey] = useState('composite_score');
  const [sortDir, setSortDir] = useState('desc');
  // DAS-2458: which all-candidates rows are expanded inline. Set of IDs.
  const [expandedAllIds, setExpandedAllIds] = useState(new Set());
  const toggleAllRowExpansion = (id) => {
    setExpandedAllIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [backtesting, setBacktesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [backtestEvents, setBacktestEvents] = useState({});
  // DAS-2413: regime-slice state
  const [expandedRegimeCandidate, setExpandedRegimeCandidate] = useState(null);
  const [regimeSlices, setRegimeSlices] = useState({});  // candidateId → slice rows
  const [slicingId, setSlicingId] = useState(null);
  const [promotingRegimeId, setPromotingRegimeId] = useState(null);
  // DAS-2416: sector-slice state
  const [expandedSectorCandidate, setExpandedSectorCandidate] = useState(null);
  const [sectorSlices, setSectorSlices] = useState({});
  const [sectorSlicingId, setSectorSlicingId] = useState(null);
  const [promotingSectorId, setPromotingSectorId] = useState(null);
  const [error, setError] = useState(null);

  // DAS-2387: configurable discovery run
  const [showRunModal, setShowRunModal] = useState(false);
  const [runNumCandidates, setRunNumCandidates] = useState(20);
  const [runAutoPipeline, setRunAutoPipeline] = useState(true);
  // DAS-2443: per-run prompt-enrichment toggles
  const [runWay1, setRunWay1] = useState(false);
  const [runWay2, setRunWay2] = useState(false);
  // Pipeline progress while auto-pipeline is executing:
  //   { phase: 'generating' | 'backtesting' | 'refining' | 'done',
  //     done: int, total: int, runId: int|null, failures: [...] }
  const [pipelineProgress, setPipelineProgress] = useState(null);

  // UX: collapsible sections for Promotion Proposals + Discovery Runs.
  // Persisted to localStorage so the layout sticks across reloads.
  const [proposalsExpanded, setProposalsExpanded] = useState(() => {
    const v = localStorage.getItem('ad_proposals_expanded');
    return v === null ? true : v === '1';
  });
  const [runsExpanded, setRunsExpanded] = useState(() => {
    const v = localStorage.getItem('ad_runs_expanded');
    return v === null ? true : v === '1';
  });
  useEffect(() => { localStorage.setItem('ad_proposals_expanded', proposalsExpanded ? '1' : '0'); }, [proposalsExpanded]);
  useEffect(() => { localStorage.setItem('ad_runs_expanded', runsExpanded ? '1' : '0'); }, [runsExpanded]);

  // DAS-2418: scoring-config state
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [scoringConfig, setScoringConfig] = useState(null);
  const [scoringDefaults, setScoringDefaults] = useState(null);
  const [scoringSaving, setScoringSaving] = useState(false);
  const [scoringError, setScoringError] = useState(null);

  // DAS-2460: archetypes editor state
  const [showArchetypesModal, setShowArchetypesModal] = useState(false);
  const [archetypes, setArchetypes] = useState([]);       // editable working copy
  const [availableFields, setAvailableFields] = useState([]); // backtestable clause fields
  const [archetypesSaving, setArchetypesSaving] = useState(false);
  const [archetypesError, setArchetypesError] = useState(null);

  // DAS-2420: autonomous-mode state
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);   // proposal currently being approved/dismissed
  const [showAutonomousModal, setShowAutonomousModal] = useState(false);
  const [autoBudgetUsd, setAutoBudgetUsd] = useState(5);
  const [autoCutoffHour, setAutoCutoffHour] = useState(5);
  const [autoCandidatesPerRun, setAutoCandidatesPerRun] = useState(10);
  // DAS-2446: exemplar mode for the autonomous loop. One of:
  //   "off"     — no exemplar block (baseline DAS-2431 archetype-quota only)
  //   "way1"    — anchor every iteration on top-K winning live alerts
  //   "way2"    — winners-vs-losers contrast every iteration
  //   "shuffle" — random pick of {off, way1, way2} per iteration
  const [autoExemplarMode, setAutoExemplarMode] = useState("off");
  const [autonomousStarted, setAutonomousStarted] = useState(null);  // {pid, log_path} | null

  // DAS-2430: amendment-modal state
  const [amendModalOpen, setAmendModalOpen] = useState(false);
  const [amendParentId, setAmendParentId] = useState(null);
  const [amendProposals, setAmendProposals] = useState([]);
  const [amendSelected, setAmendSelected] = useState(new Set());
  const [amendLoading, setAmendLoading] = useState(false);
  const [amendError, setAmendError] = useState(null);
  const [amendingRun, setAmendingRun] = useState(false);
  const [amendEligibility, setAmendEligibility] = useState({}); // {cid: {eligible, reason}}

  const fetchRuns = useCallback(async () => {
    try {
      const res = await api.get('/alert_discovery/runs');
      setRuns(res.data);
    } catch (err) {
      setError('Failed to load discovery runs');
    }
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Fetch clause id -> text mapping once on mount for tooltip rendering
  useEffect(() => {
    api.get('/alert_discovery/clauses')
      .then(res => setClauseTexts(res.data || {}))
      .catch(() => setClauseTexts({}));
  }, []);

  // 2026-05-22: fetch scoringConfig on mount so the per-candidate score
  // tooltip can render the LIVE weights (not hardcoded defaults). Was a
  // long-standing UI bug — the tooltip displayed a fake breakdown using
  // legacy defaults regardless of the user's stored config.
  useEffect(() => {
    api.get('/alert_discovery/scoring_config')
      .then(res => {
        setScoringConfig(res.data.current);
        setScoringDefaults(res.data.defaults);
      })
      .catch(() => {});  // tooltip falls back to backend defaults if fetch fails
  }, []);

  // DAS-2420: load pending proposals on mount + every 60s while page is open
  const [proposalsError, setProposalsError] = useState(null);
  const [proposalsLastLoaded, setProposalsLastLoaded] = useState(null);
  // Expanded-proposal state: which proposal is open, and cached slice data per candidate.
  const [expandedProposalId, setExpandedProposalId] = useState(null);
  const [proposalDetails, setProposalDetails] = useState({});  // candidateId -> {regime:[], sector:[]}
  const [proposalDetailsLoading, setProposalDetailsLoading] = useState(false);

  const fetchProposals = useCallback(async () => {
    setProposalsLoading(true);
    setProposalsError(null);
    try {
      const res = await api.get('/alert_discovery/proposals?status=pending&limit=100');
      setProposals(res.data.proposals || []);
      setProposalsLastLoaded(new Date());
    } catch (err) {
      // DAS-2420 fix: surface fetch errors instead of silently hiding the panel
      const code = err.response?.status;
      const msg = err.response?.data?.error || err.message || 'unknown';
      setProposalsError(`Failed to load proposals (${code || 'network'}): ${msg}`);
    } finally {
      setProposalsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProposals();
    const t = setInterval(fetchProposals, 60_000);
    return () => clearInterval(t);
  }, [fetchProposals]);

  // DAS-2418: fetch + save the composite-score config
  const openScoringModal = async () => {
    setScoringError(null);
    setShowScoringModal(true);
    try {
      const res = await api.get('/alert_discovery/scoring_config');
      setScoringConfig(res.data.current);
      setScoringDefaults(res.data.defaults);
    } catch (err) {
      setScoringError(err.response?.data?.error || 'Failed to load scoring config');
    }
  };

  const saveScoringConfig = async () => {
    setScoringSaving(true);
    setScoringError(null);
    try {
      await api.put('/alert_discovery/scoring_config', { config: scoringConfig });
      setShowScoringModal(false);
    } catch (err) {
      setScoringError(err.response?.data?.error || 'Save failed');
    } finally {
      setScoringSaving(false);
    }
  };

  const resetScoringConfig = async () => {
    if (!window.confirm('Reset all scoring parameters to their original defaults?')) return;
    setScoringSaving(true);
    setScoringError(null);
    try {
      const res = await api.post('/alert_discovery/scoring_config/reset');
      setScoringConfig(res.data.config);
    } catch (err) {
      setScoringError(err.response?.data?.error || 'Reset failed');
    } finally {
      setScoringSaving(false);
    }
  };

  // Helper to update a nested key in scoringConfig immutably
  const updateScoringField = (section, key, value) => {
    const n = Number(value);
    setScoringConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: Number.isNaN(n) ? value : n },
    }));
  };

  // ── DAS-2460: archetype editor handlers ──────────────────────────
  const openArchetypesModal = async () => {
    setArchetypesError(null);
    setArchetypes([]);
    setAvailableFields([]);
    setShowArchetypesModal(true);
    // Fast: the archetypes themselves (10 rows from one table).
    try {
      const res = await api.get('/alert_discovery/archetypes');
      const rows = res.data.archetypes || [];
      console.log('[DAS-2460] archetypes loaded:', rows.length, rows); // surfacing for debug
      setArchetypes(rows);
    } catch (err) {
      console.error('[DAS-2460] archetypes fetch failed:', err);
      setArchetypesError(err.response?.data?.error || err.message || 'Load failed');
      return;
    }
    // Slow: the backtestable clause-field list (runs extract_knowledge).
    // Fire-and-forget so the modal renders instantly with archetypes
    // even before the chip picker is ready.
    try {
      const r2 = await api.get('/alert_discovery/archetypes/available_fields');
      setAvailableFields(r2.data.available_fields || []);
    } catch (err) {
      console.warn('[DAS-2460] available_fields fetch failed (chip picker disabled):', err);
    }
  };

  const saveArchetypeRow = async (row) => {
    setArchetypesSaving(true);
    setArchetypesError(null);
    try {
      await api.put(`/alert_discovery/archetypes/${encodeURIComponent(row.name)}`, {
        label: row.label,
        theme: row.theme,
        clause_fields: row.clause_fields,  // null = "any" (free_choice)
        enabled: row.enabled ? 1 : 0,
      });
    } catch (err) {
      setArchetypesError(`Save ${row.name}: ${err.response?.data?.error || err.message}`);
    } finally {
      setArchetypesSaving(false);
    }
  };

  const toggleArchetypeEnabled = (name) => {
    setArchetypes(prev => prev.map(a => a.name === name ? { ...a, enabled: !a.enabled, _dirty: true } : a));
  };

  const updateArchetypeField = (name, field, value) => {
    setArchetypes(prev => prev.map(a => a.name === name ? { ...a, [field]: value, _dirty: true } : a));
  };

  const toggleArchetypeClauseField = (name, fieldName) => {
    setArchetypes(prev => prev.map(a => {
      if (a.name !== name) return a;
      // free_choice has clause_fields=null meaning "any" — toggling on a chip
      // converts it to a concrete list (probably never the intent for free_choice,
      // so guard).
      if (a.clause_fields === null) return a;
      const set = new Set(a.clause_fields);
      if (set.has(fieldName)) set.delete(fieldName); else set.add(fieldName);
      return { ...a, clause_fields: Array.from(set).sort(), _dirty: true };
    }));
  };

  const saveAllArchetypes = async () => {
    const dirty = archetypes.filter(a => a._dirty);
    if (!dirty.length) {
      setShowArchetypesModal(false);
      return;
    }
    setArchetypesSaving(true);
    setArchetypesError(null);
    try {
      for (const row of dirty) {
        await api.put(`/alert_discovery/archetypes/${encodeURIComponent(row.name)}`, {
          label: row.label,
          theme: row.theme,
          clause_fields: row.clause_fields,
          enabled: row.enabled ? 1 : 0,
        });
      }
      // re-fetch to get clean copies
      const res = await api.get('/alert_discovery/archetypes');
      setArchetypes(res.data.archetypes || []);
      setShowArchetypesModal(false);
    } catch (err) {
      setArchetypesError(err.response?.data?.error || 'Save failed');
    } finally {
      setArchetypesSaving(false);
    }
  };

  const addNewArchetype = async () => {
    const name = window.prompt('New archetype name (alphanumeric + underscore, used as ID):');
    if (!name) return;
    if (!/^[A-Za-z0-9_]+$/.test(name)) {
      alert('Name must be alphanumeric + underscore only');
      return;
    }
    setArchetypesSaving(true);
    setArchetypesError(null);
    try {
      await api.post('/alert_discovery/archetypes', {
        name,
        label: name.replace(/_/g, ' '),
        theme: '',
        clause_fields: [],
        enabled: 1,
        ordering: 500,
      });
      const res = await api.get('/alert_discovery/archetypes');
      setArchetypes(res.data.archetypes || []);
    } catch (err) {
      setArchetypesError(err.response?.data?.error || 'Create failed');
    } finally {
      setArchetypesSaving(false);
    }
  };

  const deleteArchetypeRow = async (name) => {
    if (name === 'free_choice') {
      alert("'free_choice' is the always-available fallback and cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete archetype '${name}'? This cannot be undone.`)) return;
    setArchetypesSaving(true);
    setArchetypesError(null);
    try {
      await api.delete(`/alert_discovery/archetypes/${encodeURIComponent(name)}`);
      const res = await api.get('/alert_discovery/archetypes');
      setArchetypes(res.data.archetypes || []);
    } catch (err) {
      setArchetypesError(err.response?.data?.error || 'Delete failed');
    } finally {
      setArchetypesSaving(false);
    }
  };

  const startAutonomousDiscovery = async () => {
    if (!window.confirm(
      `Start Autonomous Discovery?\n\n` +
      `Budget: $${autoBudgetUsd}\n` +
      `Cutoff hour: ${autoCutoffHour}:00\n` +
      `Candidates per run: ${autoCandidatesPerRun}\n\n` +
      `The loop will run in the background until budget is hit or cutoff time.`
    )) return;
    try {
      const res = await api.post('/alert_discovery/autonomous/start', {
        budget_usd: parseFloat(autoBudgetUsd),
        cutoff_hour: parseInt(autoCutoffHour, 10),
        candidates_per_run: parseInt(autoCandidatesPerRun, 10),
        exemplar_mode: autoExemplarMode,
      });
      setAutonomousStarted({ pid: res.data.pid, log_path: res.data.log_path });
      setShowAutonomousModal(false);
      window.alert(
        `✓ Autonomous loop started (PID ${res.data.pid}).\n\n` +
        `Watch this page — proposals will appear here as the night progresses.`
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start autonomous loop');
    }
  };

  // DAS-2420: safe number formatter. Slice GET endpoints return Decimal
  // values as strings ("34.50") in some Flask paths; calling .toFixed on
  // a string throws. Coerce-then-format-or-dash defends both paths.
  const fmtNum = (v, digits = 1, suffix = '') => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return n.toFixed(digits) + suffix;
  };

  // DAS-2420: shared column-header tooltips for regime + sector slice
  // tables. Single source of truth so we don't drift between the two.
  const SLICE_COL_TOOLTIPS = {
    fires: "Number of times the candidate fired in this bucket (one row per ticker-date that triggered all clauses).",
    fpy: "Fires per year — normalised to the effective backtest window (DAS-2406). Lower = rarer signal.",
    score: "Composite score 0-100. Weighted blend of 1M metrics (hit rate, avg return, fires/year, consistency) multiplied by a 1Y hit-α factor (DAS-2405).",
    hit1m: "% of fires where the ticker had a positive 1-month absolute return.",
    avgA6m: "Trimmed-mean 6-month alpha vs S&P500 (DAS-2423). Closes the 1M→1Y gap so you can see whether a 1M edge holds at the mid-horizon before betting on 1Y.",
    medA6m: "Median 6-month alpha vs S&P500. Outlier-robust mid-horizon read.",
    hitA6m: "% of fires where the ticker beat the S&P500 over 6 months. Bridges the 1M hit rate and 1Y hit rate.",
    avgA1y: "Average 1-year alpha vs S&P500. Positive = candidate beat the market on average over 1 year.",
    medA1y: "Median 1-year alpha vs S&P500. Robust to outliers — half the fires did better than this, half worse.",
    hitA1y: "% of fires where the ticker's 1-year return beat the S&P500's 1-year return over the same window. 50% = random. >55% = consistent edge. <45% = candidate underperforms the market.",
    tc: "Temporal concentration (DAS-2407): fraction of fires that fell within their densest stretch. Higher = burstier (e.g. all fires on one crisis week). Lower = spread across years. <3 unique dates = N/A.",
    tickers: "Number of distinct tickers that fired. A higher count = broader signal. < 5 unique tickers (DAS-2414/2422) means the 'pattern' is really a 1-2 stock story and the autonomous loop refuses to propose it.",
    tickerConc: "Ticker concentration (DAS-2422, Herfindahl Σpᵢ²): 1.0 = all fires on one ticker, 0.01 = uniform across 100 tickers. > 0.6 = 1-2 tickers dominate and the propose-gate rejects.",
  };

  // DAS-2420: verdict label glossary, surfaced as a tooltip on the
  // verdict pill in each proposal row.
  const VERDICT_TOOLTIPS = {
    regime_bound_BULL: "Best results in BULL regime; gap from ALL ≥ 30 pts. Promote with explicit regime=BULL gate.",
    regime_bound_BEAR: "Best results in BEAR regime; gap from ALL ≥ 30 pts. Promote with explicit regime=BEAR gate.",
    regime_bound_NEUTRAL: "Best results in NEUTRAL regime; gap from ALL ≥ 30 pts. Promote with explicit regime=NEUTRAL gate.",
    regime_bound_bursty_BULL: "Like regime_bound_BULL but fires cluster on single episodes (low TC). Accept episodic behaviour.",
    regime_bound_bursty_BEAR: "Like regime_bound_BEAR but fires cluster on single episodes (low TC). Accept episodic behaviour.",
    regime_bound_bursty_NEUTRAL: "Like regime_bound_NEUTRAL but fires cluster on single episodes (low TC). Accept episodic behaviour.",
    implicitly_gated_BULL: "≥95% of fires land in BULL by construction — the candidate's own clauses already self-gate. No extra regime clause needed; promote as-is.",
    implicitly_gated_BEAR: "≥95% of fires land in BEAR by construction — the candidate's own clauses already self-gate. No extra regime clause needed; promote as-is.",
    implicitly_gated_NEUTRAL: "≥95% of fires land in NEUTRAL by construction — the candidate's own clauses already self-gate. No extra regime clause needed; promote as-is.",
    no_regime_dominance: "Signal is roughly universal — no regime materially outperforms. Promote ungated if score warrants.",
    false_promise: "ALL composite looks decent but no regime carries enough quality fires to justify promotion.",
    insufficient_data: "Best regime has too few fires (<10) for reliable classification — refine first.",
  };

  // DAS-2420: click a proposal row to expand details. Fetches regime + sector
  // slice from cache (GET endpoints — no recompute) on first open.
  const toggleProposalExpansion = async (proposal) => {
    if (expandedProposalId === proposal.id) {
      setExpandedProposalId(null);
      return;
    }
    setExpandedProposalId(proposal.id);
    if (!proposalDetails[proposal.candidate_id]) {
      setProposalDetailsLoading(true);
      try {
        const [regimeRes, sectorRes] = await Promise.all([
          api.get(`/alert_discovery/slice_by_regime/${proposal.candidate_id}`).catch(() => ({ data: { slices: [] } })),
          api.get(`/alert_discovery/slice_by_sector/${proposal.candidate_id}`).catch(() => ({ data: { slices: [] } })),
        ]);
        setProposalDetails(prev => ({
          ...prev,
          [proposal.candidate_id]: {
            regime: regimeRes.data.slices || [],
            sector: sectorRes.data.slices || [],
          },
        }));
      } finally {
        setProposalDetailsLoading(false);
      }
    }
  };

  const approveProposal = async (proposal) => {
    const secOp = proposal.suggested_sector_gate_op || '=';
    const sectorPart = proposal.suggested_sector_gate
      ? (secOp === '!='
          ? `EXCLUDE sector "${proposal.suggested_sector_gate}" (sector ≠ ${proposal.suggested_sector_gate})`
          : `include only sector "${proposal.suggested_sector_gate}" (sector = ${proposal.suggested_sector_gate})`)
      : null;
    const gateDesc = [
      proposal.suggested_regime_gate && `regime=${proposal.suggested_regime_gate}`,
      sectorPart,
    ].filter(Boolean).join(', ') || 'ungated';
    if (!window.confirm(
      `Approve proposal #${proposal.id}?\n\n` +
      `Candidate: #${proposal.candidate_id} (${proposal.alert_type})\n` +
      `Gates: ${gateDesc}\n` +
      `Composite score: ${proposal.suggested_composite_score}\n\n` +
      `This creates a LIVE alert in Market_Alert_Definitions. Continue?`
    )) return;
    setReviewingId(proposal.id);
    try {
      const res = await api.post(`/alert_discovery/proposals/${proposal.id}/approve`);
      await fetchProposals();
      window.alert(`✓ Promoted as alert #${res.data.alert_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Approve failed');
    } finally {
      setReviewingId(null);
    }
  };

  const dismissProposal = async (proposal) => {
    if (!window.confirm(
      `Dismiss proposal #${proposal.id}?\n\n` +
      `No live alert will be created. The proposal is marked dismissed and won't appear again.`
    )) return;
    setReviewingId(proposal.id);
    try {
      await api.post(`/alert_discovery/proposals/${proposal.id}/dismiss`);
      await fetchProposals();
    } catch (err) {
      setError(err.response?.data?.error || 'Dismiss failed');
    } finally {
      setReviewingId(null);
    }
  };

  const fetchCandidates = useCallback(async (runId) => {
    setLoading(true);
    try {
      const res = await api.get(`/alert_discovery/candidates/${runId}`);
      setCandidates(res.data);
      setSelectedRunId(runId);
      // UX: collapse the runs table so the newly-loaded candidates
      // panel slides into the visible area instead of being below a
      // long list of runs. User can re-expand the runs table via the
      // header chevron.
      setRunsExpanded(false);
    } catch (err) {
      setError('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, []);

  // DAS-2458: load every candidate across all runs (joined w/ results)
  // for the cross-run sortable table.
  const fetchAllCandidates = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await api.get('/alert_discovery/candidates_all');
      setAllCandidates(res.data);
    } catch (err) {
      setError('Failed to load cross-run candidates');
    } finally {
      setLoadingAll(false);
    }
  }, []);

  // DAS-2458: header click → toggle sort. Numbers sort numerically,
  // everything else as a string. Nulls always sink to the bottom.
  const requestSort = useCallback((key) => {
    setSortKey(prev => {
      if (prev === key) {
        // toggle direction
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      // new column → default to descending for most metrics, ascending for id
      setSortDir(key === 'id' || key === 'run_id' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const sortedAllCandidates = React.useMemo(() => {
    if (!allCandidates.length) return allCandidates;
    const copy = allCandidates.slice();
    copy.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      // nulls sink
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [allCandidates, sortKey, sortDir]);

  // DAS-2458: cross-run candidate-ID lookup. Hits the /locate endpoint
  // to find the candidate's run_id, then opens that run + sets the
  // in-run ID filter so the matching row is the only one visible.
  const locateCandidate = useCallback(async () => {
    const raw = (globalCandidateLookup || '').trim();
    if (!raw) return;
    const id = parseInt(raw, 10);
    if (!Number.isFinite(id) || id <= 0) {
      setLocateError('numeric ID required');
      return;
    }
    setLocateError('');
    setLocatingCandidate(true);
    try {
      const res = await api.get(`/alert_discovery/candidate/${id}/locate`);
      const runId = res.data.run_id;
      await fetchCandidates(runId);
      // Pin the in-run filter to the located ID so the table shows
      // ONLY that candidate. User clears the in-run filter to see siblings.
      setCandidateIdSearch(String(id));
      // Auto-expand the candidate row if your row click does that —
      // best-effort: setExpandedCandidate exists for the popover.
      try { setExpandedCandidate(id); } catch (_e) { /* harmless */ }
      setGlobalCandidateLookup('');
    } catch (err) {
      if (err?.response?.status === 404) {
        setLocateError(`#${id} not found`);
      } else {
        setLocateError('lookup failed');
      }
    } finally {
      setLocatingCandidate(false);
    }
  }, [globalCandidateLookup, fetchCandidates]);

  const deleteRun = async (runId) => {
    if (!window.confirm(`Delete run #${runId} and ALL its candidates, events, and results? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await api.delete(`/alert_discovery/runs/${runId}`);
      if (selectedRunId === runId) {
        setSelectedRunId(null);
        setCandidates([]);
        setSelectedCandidates(new Set());
      }
      setSelectedRuns(prev => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
      await fetchRuns();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const toggleRunSelection = (runId) => {
    setSelectedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const bulkDeleteRuns = async () => {
    if (selectedRuns.size === 0) return;
    const ids = Array.from(selectedRuns);
    if (!window.confirm(`Delete ${ids.length} run(s) and ALL their candidates, events, and results? This cannot be undone.`)) {
      return;
    }
    setError(null);
    let failures = [];
    for (const id of ids) {
      try {
        await api.delete(`/alert_discovery/runs/${id}`);
      } catch (err) {
        failures.push(id);
      }
    }
    if (selectedRunId && ids.includes(selectedRunId)) {
      setSelectedRunId(null);
      setCandidates([]);
      setSelectedCandidates(new Set());
    }
    setSelectedRuns(new Set());
    await fetchRuns();
    if (failures.length) {
      setError(`Failed to delete run(s): ${failures.join(', ')}`);
    }
  };

  const startDiscoveryRun = async () => {
    // DAS-2387: open the config modal; the actual run is kicked off by
    // executeDiscoveryRun() when the user clicks Start in the modal.
    setShowRunModal(true);
  };

  // DAS-2387: orchestrated end-to-end pipeline.
  // Phase 1 — generate (POST /run). Always.
  // Phase 2 — backtest all newly-generated candidates (if auto-pipeline).
  // Phase 3 — refine every candidate that fired in phase 2 (if auto-pipeline).
  // Errors are non-fatal: a failed step is logged into `failures` and the
  // pipeline continues. Final summary is shown via the error banner if any
  // failures occurred.
  const executeDiscoveryRun = async () => {
    const n = parseInt(runNumCandidates, 10);
    if (!Number.isInteger(n) || n < 1 || n > 50) {
      setError('Number of candidates must be an integer between 1 and 50');
      return;
    }
    const auto = runAutoPipeline;
    setShowRunModal(false);
    setError(null);
    setGenerating(true);
    setPipelineProgress({ phase: 'generating', done: 0, total: 1, runId: null, failures: [] });

    let runId = null;
    let generatedCandidates = [];
    const failures = [];

    // --- Phase 1: generate ---
    try {
      const res = await api.post('/alert_discovery/run', {
        num_candidates: n,
        enable_way1_exemplars: runWay1,
        enable_way2_contrastive: runWay2,
      });
      runId = res.data.run_id;
      await fetchRuns();
      await fetchCandidates(runId);
    } catch (err) {
      setError(err.response?.data?.error || 'Discovery run failed');
      setGenerating(false);
      setPipelineProgress(null);
      return;
    }
    setGenerating(false);

    if (!auto) {
      // Manual mode — caller can now click Backtest Selected / Refine.
      setPipelineProgress(null);
      return;
    }

    // Re-fetch candidates fresh so we know all proposed IDs to backtest.
    try {
      const res = await api.get(`/alert_discovery/candidates/${runId}`);
      generatedCandidates = res.data || [];
      setCandidates(generatedCandidates);
    } catch (err) {
      setError('Auto-pipeline aborted: could not load generated candidates');
      setPipelineProgress(null);
      return;
    }

    const proposedIds = generatedCandidates
      .filter(c => c.status === 'proposed')
      .map(c => c.id);

    // --- Phase 2: backtest all proposed ---
    if (proposedIds.length > 0) {
      setPipelineProgress({ phase: 'backtesting', done: 0, total: proposedIds.length, runId, failures });
      try {
        await api.post('/alert_discovery/backtest', { candidate_ids: proposedIds });
        await fetchCandidates(runId);
      } catch (err) {
        failures.push({ phase: 'backtest', error: err.response?.data?.error || 'unknown' });
      }
      setPipelineProgress(prev => ({ ...prev, done: proposedIds.length, failures }));
    }

    // Re-fetch to find which candidates actually fired (eligible for refine).
    let refreshed = [];
    try {
      const res = await api.get(`/alert_discovery/candidates/${runId}`);
      refreshed = res.data || [];
      setCandidates(refreshed);
    } catch (err) {
      failures.push({ phase: 'reload-after-backtest', error: 'fetch failed' });
    }
    const refineTargets = refreshed.filter(
      c => c.status === 'backtested' && (c.total_fires ?? 0) > 0 && c.is_refined !== 1
    );

    // --- Phase 3: refine all that fired ---
    if (refineTargets.length > 0) {
      setPipelineProgress({ phase: 'refining', done: 0, total: refineTargets.length, runId, failures });
      for (let i = 0; i < refineTargets.length; i++) {
        const c = refineTargets[i];
        setRefiningId(c.id);
        try {
          await api.post(`/alert_discovery/refine/${c.id}`, { sample_frequency_days: 7 });
        } catch (err) {
          failures.push({
            phase: 'refine',
            candidate_id: c.id,
            error: err.response?.data?.error || 'unknown',
          });
        }
        setPipelineProgress(prev => ({ ...prev, done: i + 1, failures }));
        await fetchCandidates(runId);
      }
      setRefiningId(null);
    }

    // --- Phase 4: propose (DAS-2444) ---
    // Apply the same propose-gate / dedup / insert logic the autonomous
    // loop uses, so manual runs can also produce morning-list proposals
    // for candidates that score above propose_min_score.
    setPipelineProgress({ phase: 'proposing', done: 0, total: 1, runId, failures });
    try {
      const res = await api.post(`/alert_discovery/run/${runId}/propose-pass`);
      const s = res.data || {};
      if (s.proposed > 0) {
        // Trigger a fetch so the user sees the new proposals appear.
        fetchProposals();
      }
      // Push a friendly summary line into failures (used as the
      // pipeline-done banner) so the user sees what happened.
      if ((s.errors || []).length > 0) {
        failures.push({
          phase: 'propose',
          error: `${s.errors.length} candidate error(s) during propose pass`,
        });
      }
      console.log(
        `[propose-pass] run ${runId}: evaluated=${s.evaluated} `
        + `proposed=${s.proposed} skipped_existing=${s.skipped_existing} `
        + `skipped_gate=${s.skipped_gate} skipped_dedup=${s.skipped_dedup}`
      );
    } catch (err) {
      failures.push({ phase: 'propose-pass', error: err.response?.data?.error || 'unknown' });
    }
    await fetchRuns();
    await fetchCandidates(runId);

    // --- Done ---
    setPipelineProgress({ phase: 'done', done: 0, total: 0, runId, failures });
    if (failures.length > 0) {
      setError(`Pipeline finished with ${failures.length} failure(s): ` +
        failures.map(f => `[${f.phase}${f.candidate_id ? ` #${f.candidate_id}` : ''}] ${f.error}`).join('; '));
    }
    setTimeout(() => setPipelineProgress(null), 4000);
  };

  const toggleCandidate = (id) => {
    setSelectedCandidates(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const proposedIds = candidates.filter(c => c.status === 'proposed').map(c => c.id);
    setSelectedCandidates(new Set(proposedIds));
  };

  const runBacktest = async () => {
    if (selectedCandidates.size === 0) return;
    setBacktesting(true);
    setError(null);
    try {
      await api.post('/alert_discovery/backtest', {
        candidate_ids: Array.from(selectedCandidates),
      });
      await fetchCandidates(selectedRunId);
      setSelectedCandidates(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Backtest failed');
    } finally {
      setBacktesting(false);
    }
  };

  const promoteCandidate = async (candidate) => {
    const candidateId = candidate.id;
    const msg = `Approve candidate #${candidateId}?\n\n` +
      `This will create a LIVE alert in Market_Alert_Definitions ` +
      `(alert_subtype='AI-Discovered', status=1 active immediately).\n\n` +
      `Type: ${candidate.alert_type}\n` +
      `Clauses: ${(candidate.clauses_json || []).join(', ')}\n` +
      `Fires: ${candidate.total_fires} (${candidate.fires_per_year}/yr)\n` +
      `Hit rate 1m: ${candidate.hit_rate_1m}%\n` +
      `Alpha vs S&P 1m: ${candidate.avg_alpha_sp500_1m}%\n` +
      `Refined: ${candidate.is_refined ? `yes (${candidate.sample_frequency_days}d sampling)` : 'NO'}\n\n` +
      `The next daily DR_Alert run will start firing this alert.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/alert_discovery/promote/${candidateId}`);
      await fetchCandidates(selectedRunId);
    } catch (err) {
      setError(err.response?.data?.error || 'Promotion failed');
    }
  };

  const canApprove = (c) =>
    c.status === 'backtested' &&
    c.total_fires > 0 &&
    c.is_refined === 1;

  const [refiningId, setRefiningId] = useState(null);
  const [refineAllProgress, setRefineAllProgress] = useState(null);  // {done, total} or null

  const refineCandidate = async (candidateId) => {
    setRefiningId(candidateId);
    setError(null);
    try {
      await api.post(`/alert_discovery/refine/${candidateId}`, { sample_frequency_days: 7 });
      await fetchCandidates(selectedRunId);
      if (expandedCandidate === candidateId) {
        const res = await api.get(`/alert_discovery/backtest_events/${candidateId}`);
        setBacktestEvents(prev => ({ ...prev, [candidateId]: res.data }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Refinement failed');
    } finally {
      setRefiningId(null);
    }
  };

  // DAS-2430: amendment workflow ----------------------------------------
  const checkAmendEligible = async (candidateId) => {
    try {
      const res = await api.get(`/alert_discovery/amend/${candidateId}/eligible`);
      setAmendEligibility(prev => ({ ...prev, [candidateId]: res.data }));
      return res.data;
    } catch (err) {
      const fail = { eligible: false, reason: err.response?.data?.error || 'check failed' };
      setAmendEligibility(prev => ({ ...prev, [candidateId]: fail }));
      return fail;
    }
  };

  const openAmendModal = async (candidateId) => {
    setAmendError(null);
    setAmendProposals([]);
    setAmendSelected(new Set());
    setAmendParentId(candidateId);
    setAmendModalOpen(true);
    setAmendLoading(true);
    try {
      const res = await api.post(`/alert_discovery/amend/${candidateId}/propose`);
      const items = res.data?.amendments || [];
      setAmendProposals(items);
      // Pre-select ALL — user un-checks ones they don't want.
      setAmendSelected(new Set(items.map((_, i) => i)));
    } catch (err) {
      setAmendError(err.response?.data?.error || 'Failed to propose amendments');
    } finally {
      setAmendLoading(false);
    }
  };

  const spawnSelectedAmendments = async () => {
    if (!amendParentId || amendSelected.size === 0) return;
    const chosen = Array.from(amendSelected).sort((a, b) => a - b).map(i => amendProposals[i]);
    setAmendLoading(true);
    setAmendError(null);
    try {
      const res = await api.post(
        `/alert_discovery/amend/${amendParentId}/spawn`,
        { amendments: chosen },
      );
      const spawned = res.data?.spawned || [];
      const skipped = res.data?.skipped || [];
      let msg = `Spawned ${spawned.length} variant${spawned.length === 1 ? '' : 's'} (${spawned.join(', ')}).`;
      if (skipped.length) msg += ` ${skipped.length} skipped (dup or invalid).`;
      alert(msg + ' They are queued — backtest them via "Backtest Selected" or wait for the next auto/autonomous cycle.');
      setAmendModalOpen(false);
      await fetchCandidates(selectedRunId);
    } catch (err) {
      setAmendError(err.response?.data?.error || 'Spawn failed');
    } finally {
      setAmendLoading(false);
    }
  };

  const amendAllEligibleInRun = async () => {
    if (!selectedRunId) return;
    if (!window.confirm(
      `Amend ALL eligible candidates in run ${selectedRunId}? ` +
      `This calls the LLM once per eligible candidate (~$0.05 each) and ` +
      `spawns up to 3 variant candidates per eligible parent.`
    )) return;
    setAmendingRun(true);
    setError(null);
    try {
      const res = await api.post(`/alert_discovery/amend/run/${selectedRunId}`);
      const results = res.data?.results || {};
      const ids = Object.keys(results);
      let totalSpawned = 0;
      let eligibleCount = 0;
      for (const r of Object.values(results)) {
        if (r.eligible) {
          eligibleCount += 1;
          totalSpawned += (r.spawned || []).length;
        }
      }
      alert(
        `Run-level amendment complete:\n` +
        `  ${ids.length} candidates evaluated\n` +
        `  ${eligibleCount} eligible\n` +
        `  ${totalSpawned} variants spawned`
      );
      await fetchCandidates(selectedRunId);
    } catch (err) {
      setError(err.response?.data?.error || 'Run-level amendment failed');
    } finally {
      setAmendingRun(false);
    }
  };

  // DAS-2413: compute (or recompute) the 4-regime slice for a candidate
  // and show the comparison row.
  const sliceByRegime = async (candidateId) => {
    if (expandedRegimeCandidate === candidateId) {
      // Toggle off
      setExpandedRegimeCandidate(null);
      return;
    }
    setSlicingId(candidateId);
    setError(null);
    try {
      // POST triggers computation if not yet done; the response is
      // also returned so we get fresh data even on first slice.
      const res = await api.post(`/alert_discovery/slice_by_regime/${candidateId}`);
      setRegimeSlices(prev => ({ ...prev, [candidateId]: res.data.slices }));
      setExpandedRegimeCandidate(candidateId);
    } catch (err) {
      setError(err.response?.data?.error || 'Slice failed');
    } finally {
      setSlicingId(null);
    }
  };

  // DAS-2416: compute the 4-10 row sector slice for a candidate.
  const sliceBySector = async (candidateId) => {
    if (expandedSectorCandidate === candidateId) {
      setExpandedSectorCandidate(null);
      return;
    }
    setSectorSlicingId(candidateId);
    setError(null);
    try {
      const res = await api.post(`/alert_discovery/slice_by_sector/${candidateId}`);
      setSectorSlices(prev => ({ ...prev, [candidateId]: res.data.slices }));
      setExpandedSectorCandidate(candidateId);
    } catch (err) {
      setError(err.response?.data?.error || 'Sector slice failed');
    } finally {
      setSectorSlicingId(null);
    }
  };

  // DAS-2416: promote a sector-gated alert.
  const promoteWithSector = async (candidate, sector) => {
    const msg = `Promote candidate #${candidate.id} as a "${sector}"-sector alert?\n\n` +
      `This creates a LIVE alert with a "sector = ${sector}" clause prepended,\n` +
      `so the alert only fires on tickers in that sector.\n\n` +
      `Type: ${candidate.alert_type}\n` +
      `Subtype tag: ... | ${sector}\n\n` +
      `Continue?`;
    if (!window.confirm(msg)) return;
    setPromotingSectorId(`${candidate.id}:${sector}`);
    setError(null);
    try {
      const res = await api.post(
        `/alert_discovery/promote_with_sector/${candidate.id}`,
        { sector },
      );
      await fetchCandidates(selectedRunId);
      window.alert(`✓ Promoted as alert #${res.data.alert_id} (${sector}-gated)`);
    } catch (err) {
      setError(err.response?.data?.error || 'Sector promotion failed');
    } finally {
      setPromotingSectorId(null);
    }
  };

  // DAS-2413: promote a regime-gated alert. The promoter prepends the
  // corresponding "Market regime IS <X>" clause to the candidate's
  // clauses before INSERTing to Market_Alert_Definitions.
  const promoteWithRegime = async (candidate, regime) => {
    const msg = `Promote candidate #${candidate.id} as a ${regime}-gated alert?\n\n` +
      `This creates a LIVE alert in Market_Alert_Definitions with an extra\n` +
      `"Market regime is currently ${regime}" clause prepended — so the alert\n` +
      `only fires when the live regime matches.\n\n` +
      `Type: ${candidate.alert_type}\n` +
      `Subtype tag: ... | ${regime}\n` +
      `Original clauses: ${(candidate.clauses_json || []).join(', ')} + regime clause\n\n` +
      `Continue?`;
    if (!window.confirm(msg)) return;
    setPromotingRegimeId(`${candidate.id}:${regime}`);
    setError(null);
    try {
      const res = await api.post(
        `/alert_discovery/promote_with_regime/${candidate.id}`,
        { regime },
      );
      await fetchCandidates(selectedRunId);
      // Keep the slice row visible so user sees the promotion took
      window.alert(`✓ Promoted as alert #${res.data.alert_id} (${regime} gate)`);
    } catch (err) {
      setError(err.response?.data?.error || 'Regime promotion failed');
    } finally {
      setPromotingRegimeId(null);
    }
  };

  // Eligible for batch refine: backtested AND fired (has a real score).
  // Excludes already-refined ones (no point re-refining) and 0-fire ones.
  const refineAllEligible = () =>
    (candidates || []).filter(c =>
      c.status === 'backtested' && (c.total_fires ?? 0) > 0 && c.is_refined !== 1
    );

  const refineAll = async () => {
    const eligible = refineAllEligible();
    if (eligible.length === 0) {
      setError('No candidates eligible for refine (need backtested status + fires > 0 + not already refined)');
      return;
    }
    const mins = Math.ceil(eligible.length * 2);  // rough estimate, 7d sampling ~2 min each
    if (!window.confirm(
      `Refine ${eligible.length} backtested candidate(s) at 7-day sampling?\n` +
      `Estimated time: ~${mins} minutes total (serial execution).\n` +
      `Already-refined candidates are skipped automatically.`
    )) return;

    setError(null);
    setRefineAllProgress({ done: 0, total: eligible.length, failures: [] });
    for (let i = 0; i < eligible.length; i++) {
      const c = eligible[i];
      setRefiningId(c.id);
      try {
        await api.post(`/alert_discovery/refine/${c.id}`, { sample_frequency_days: 7 });
      } catch (err) {
        setRefineAllProgress(prev => ({
          ...prev,
          failures: [...(prev?.failures || []), { id: c.id, error: err.response?.data?.error || 'unknown' }]
        }));
      }
      setRefineAllProgress(prev => ({ ...prev, done: i + 1 }));
      // Refresh after each so the user sees progress in the table
      await fetchCandidates(selectedRunId);
    }
    setRefiningId(null);
    // Hold final state for 3 seconds so the user can see "N of N done"
    setTimeout(() => setRefineAllProgress(null), 3000);
  };

  const beatsBenchmarks = (c) =>
    c.status === 'backtested' &&
    c.avg_alpha_sp500_1m != null && c.avg_alpha_sp500_1m > 0 &&
    c.avg_alpha_msci_1m != null && c.avg_alpha_msci_1m > 0;

  const loadBacktestEvents = async (candidateId) => {
    if (expandedCandidate === candidateId) {
      setExpandedCandidate(null);
      return;
    }
    try {
      const res = await api.get(`/alert_discovery/backtest_events/${candidateId}`);
      setBacktestEvents(prev => ({ ...prev, [candidateId]: res.data }));
      setExpandedCandidate(candidateId);
    } catch (err) {
      setError('Failed to load backtest events');
    }
  };

  const formatPct = (val) => {
    if (val == null) return '—';
    return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
  };

  // DAS-2406: render fires/yr with a tooltip exposing the effective
  // backtest window. Tier-A candidates have an effective_backtest_start of
  // 2025-04-05 (not 2020-01-01) because their fields don't exist in
  // historical data before then — and fires/yr is now normalised against
  // that window. Without the tooltip the user has no clue why a Tier-A
  // candidate at 60/yr looks "different" from a pre-Tier-A candidate at
  // 60/yr — the former is 60 fires over 13 months, the latter is 60
  // over 6 years.
  const renderFiresPerYearCell = (c) => {
    const fpy = c.fires_per_year;
    const effStart = c.effective_backtest_start;
    if (fpy == null) return '—';
    if (!effStart) return fpy.toFixed(1);
    let months = null;
    try {
      const d = new Date(effStart);
      const now = new Date();
      months = (now - d) / (1000 * 60 * 60 * 24 * 30.4375);
    } catch (_e) {}
    // DAS-2407: temporal_concentration in [0,1]. <0.20 = warning.
    const tc = c.temporal_concentration;
    const tcWarn = tc != null && tc < 0.20;
    const tcBand = tc == null
      ? null
      : tc >= 0.50 ? 'spread'
      : tc >= 0.20 ? 'moderate'
      : 'clustered';
    // DAS-2407: plain-English verdict per band
    const tcVerdict = tc == null ? null
      : tc >= 0.50
        ? { color: '#27ae60', label: 'Well spread', headline: 'Fires consistently across the backtest window — a stable, regime-independent signal.' }
      : tc >= 0.20
        ? { color: '#e6a700', label: 'Somewhat uneven', headline: 'Fires lean toward certain periods. Promote with care — live behavior may be lumpy.' }
        : { color: '#e74c3c', label: 'Clustered — caution', headline: '70% of fires happened in a tiny fraction of the window. This is a one-episode signal: in production it will likely fire in bursts then go silent for long stretches. Backtest stats (α, fires/yr) don\'t generalize. Not recommended for promotion as-is.' };
    const tooltip = (
      <div style={{ fontSize: '0.85rem', lineHeight: 1.45, minWidth: 300, maxWidth: 380 }}>
        <div><strong>Backtest window:</strong> {effStart} → today</div>
        {months != null && (
          <div><strong>Length:</strong> ~{months.toFixed(1)} months</div>
        )}
        {tcVerdict && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #555' }}>
            <div>
              <strong>How spread out are the fires?</strong>{' '}
              <span style={{ color: tcVerdict.color, fontWeight: 600 }}>
                {(tc * 100).toFixed(0)}% — {tcVerdict.label}
              </span>
            </div>
            <div style={{ marginTop: 6, color: tcVerdict.color }}>
              {tcVerdict.headline}
            </div>
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#aaa' }}>
              Reading: 70% of this candidate's fires happened in just
              {' '}{(tc * 100).toFixed(0)}% of the window.
              Higher is better. ≥50% = green, 20-50% = yellow, &lt;20% = red.
            </div>
          </div>
        )}
        {tc == null && (
          <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#aaa' }}>
            Concentration metric requires ≥3 fires.
          </div>
        )}
      </div>
    );
    const cellStyle = {
      cursor: 'help',
      borderBottom: tcWarn ? '1px dashed #e74c3c' : 'none',
    };
    return (
      <Tooltip arrow placement="right" title={tooltip}>
        <span style={cellStyle}>
          {fpy.toFixed(1)}
          {tcWarn && <span style={{ color: '#e74c3c', marginLeft: 4 }}>⚠</span>}
        </span>
      </Tooltip>
    );
  };

  // DAS-2404: render an α cell with a tooltip showing both the trimmed-mean
  // (`avg_alpha_*` — the new outlier-robust default) and the median
  // (`median_alpha_*` — most outlier-robust). When the two diverge a lot,
  // the candidate's α is being skewed by outliers and the user should be
  // skeptical of the avg.
  const renderAlphaCell = (c, suffix) => {
    const avg = c[`avg_alpha_${suffix}`];
    const med = c[`median_alpha_${suffix}`];
    if (avg == null && med == null) return '—';
    const divergence = (avg != null && med != null) ? Math.abs(avg - med) : null;
    const flag = divergence != null && divergence > 30;  // >30pct gap = outlier-suspect
    const tooltip = (
      <div style={{ fontSize: '0.85rem', lineHeight: 1.4, minWidth: 220 }}>
        <div><strong>Trimmed mean (10/10%):</strong> {formatPct(avg)}</div>
        <div><strong>Median:</strong> {formatPct(med)}</div>
        {flag && (
          <div style={{ marginTop: 6, color: '#e6a700' }}>
            ⚠ Large gap → outlier-influenced
          </div>
        )}
        <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#aaa' }}>
          DAS-2404: avg is the trimmed mean (drop top/bottom 10% of fires)
          rather than the raw arithmetic mean, to suppress microcap-rebound
          outliers. Falls back to median when n &lt; 10.
        </div>
      </div>
    );
    return (
      <Tooltip arrow placement="right" title={tooltip}>
        <span style={{ cursor: 'help', borderBottom: flag ? '1px dashed #e6a700' : 'none' }}>
          {formatPct(avg)}
        </span>
      </Tooltip>
    );
  };

  // DAS-2383: replicate the Python composite formula client-side so we can
  // show per-candidate component breakdown on hover. Mirrors
  // sandbox_backtester._compute_composite_score exactly.
  // 2026-05-22: rewritten to mirror sandbox_backtester._compute_composite_score
  // EXACTLY — reads the live `scoringConfig` state (populated on mount from
  // alert_discovery_scoring_config). Pre-rewrite the tooltip hardcoded
  // legacy weights (hit_1m=30, avg_return_1m=30, fpy=20, consistency=20),
  // which disagreed with the user's stored config and produced a Final
  // that didn't match the score column.
  const computeScoreBreakdown = (c) => {
    // Use live config if available; otherwise fall back to backend defaults
    // so the tooltip is never blank.
    const cfg = scoringConfig || scoringDefaults || {};
    const weights = cfg.weights || {};
    const band = cfg.fires_per_year_band || { lo: 5, hi: 50 };
    const cov = cfg.coverage || { low_threshold: 0.30, high_threshold: 0.70, thin_data_multiplier: 0.5 };
    const th = cfg.hit_alpha_1y_thresholds || { high: 55, medium: 45 };
    const mults = cfg.multipliers || { high: 1.0, medium: 0.85, low: 0.5 };

    const parts = [];
    let scoreSum = 0;
    let weightSum = 0;

    // Helper to add a component if its weight is non-zero and the metric exists.
    const addLinear = (label, value, key, scale) => {
      // Normaliser: clamp value/100 → [0,1]. Used by hit-rates which are 0-100.
      const w = weights[key] || 0;
      if (!w || value == null) return;
      const n = Math.min(value / scale, 1.0);
      const contrib = n * w;
      scoreSum += contrib;
      weightSum += w;
      parts.push({ label: `${label} (${value.toFixed(2)}${scale === 100 ? '%' : ''})`, weight: w, contrib });
    };
    const addSigned = (label, value, key, scale) => {
      // Normaliser for avg returns / avg alphas: clamp value/scale → [-1,1] then
      // shift to [0,1]. So 0% return = 0.5, +scale% = 1.0, -scale% = 0.
      const w = weights[key] || 0;
      if (!w || value == null) return;
      const clamped = Math.max(Math.min(value / scale, 1), -1);
      const n = (clamped + 1) / 2;
      const contrib = n * w;
      scoreSum += contrib;
      weightSum += w;
      parts.push({ label: `${label} (${value >= 0 ? '+' : ''}${value.toFixed(2)}%)`, weight: w, contrib });
    };

    // Hit rates (0-100 scale)
    addLinear('Hit rate 1m', c.hit_rate_1m, 'hit_1m', 100);
    addLinear('Hit rate 6m', c.hit_rate_6m, 'hit_6m', 100);
    addLinear('Hit rate 1y', c.hit_rate_1y, 'hit_1y', 100);
    // Avg returns (clamped at ±5% for 1m, ±20% for 6m, ±40% for 1y)
    addSigned('Avg return 1m', c.avg_return_1m, 'avg_return_1m', 5);
    addSigned('Avg return 6m', c.avg_return_6m, 'avg_return_6m', 20);
    addSigned('Avg return 1y', c.avg_return_1y, 'avg_return_1y', 40);
    // Alpha-vs-S&P hit rates
    addLinear('Hit α vs S&P 1m', c.hit_rate_alpha_sp500_1m, 'hit_alpha_sp500_1m', 100);
    addLinear('Hit α vs S&P 6m', c.hit_rate_alpha_sp500_6m, 'hit_alpha_sp500_6m', 100);
    addLinear('Hit α vs S&P 1y', c.hit_rate_alpha_sp500_1y, 'hit_alpha_sp500_1y', 100);
    // Alpha-vs-S&P avg
    addSigned('Avg α vs S&P 1m', c.avg_alpha_sp500_1m, 'avg_alpha_sp500_1m', 5);
    addSigned('Avg α vs S&P 6m', c.avg_alpha_sp500_6m, 'avg_alpha_sp500_6m', 20);
    addSigned('Avg α vs S&P 1y', c.avg_alpha_sp500_1y, 'avg_alpha_sp500_1y', 40);

    // Frequency (banded — full credit inside [lo, hi], linear ramps outside)
    const fpyW = weights.fires_per_year || 0;
    const fpy = c.fires_per_year ?? 0;
    if (fpyW && fpy > 0) {
      let f;
      if (fpy >= band.lo && fpy <= band.hi) f = 1.0;
      else if (fpy < band.lo) f = fpy / band.lo;
      else f = Math.max(0, 1 - (fpy - band.hi) / 200);
      const contrib = f * fpyW;
      scoreSum += contrib;
      weightSum += fpyW;
      parts.push({ label: `Fires per year (${fpy.toFixed(1)})`, weight: fpyW, contrib });
    }

    // Consistency (inverse of std_1m, clamped at 0 for std >= 15)
    const consW = weights.consistency || 0;
    if (consW && c.std_return_1m != null) {
      const cons = Math.max(0, 1 - c.std_return_1m / 15);
      const contrib = cons * consW;
      scoreSum += contrib;
      weightSum += consW;
      parts.push({ label: `Consistency (1/std_1m=${c.std_return_1m.toFixed(2)})`, weight: consW, contrib });
    }

    if (weightSum === 0) return null;
    const base = (scoreSum / weightSum) * 100;

    // --- Multiplier (DAS-2424 + DAS-2437 window-aware) ---
    // Bucket from hit_rate_alpha_sp500_1y vs thresholds, multiplier from coverage_1y.
    // 2026-05-22: window-aware coverage normalisation — a candidate whose
    // first fire is N days ago can't have coverage_1y > (window-365)/window.
    // We compare actual coverage against max-possible-coverage for the window.
    const hitA1y = c.hit_rate_alpha_sp500_1y;
    const cov1y = c.coverage_1y;
    const rawCov = (cov1y == null) ? 0 : Number(cov1y);

    let normCov = rawCov;
    let windowDays = null;
    if (c.effective_backtest_start) {
      try {
        const start = new Date(c.effective_backtest_start);
        const today = new Date();
        windowDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));
        if (windowDays > 365) {
          const maxPossible = (windowDays - 365) / windowDays;
          if (maxPossible > 0) {
            normCov = Math.min(rawCov / maxPossible, 1.0);
          }
        }
        // If window <= 365, no fire can possibly have full-horizon data
        // → backend returns thin_data_multiplier directly.
      } catch (e) { /* leave normCov = rawCov */ }
    }

    let bucketMult, multiplierBand;
    if (hitA1y == null) {
      bucketMult = cov.thin_data_multiplier; multiplierBand = 'insufficient-data';
    } else if (hitA1y >= th.high) {
      bucketMult = mults.high; multiplierBand = 'consistent-beat';
    } else if (hitA1y >= th.medium) {
      bucketMult = mults.medium; multiplierBand = 'toss-up';
    } else {
      bucketMult = mults.low; multiplierBand = 'loses-to-market';
    }

    let multiplier, coverageBand;
    if (windowDays != null && windowDays <= 365) {
      // Window too short for 1Y horizon
      multiplier = cov.thin_data_multiplier;
      coverageBand = 'window-too-short';
    } else if (normCov < cov.low_threshold) {
      multiplier = cov.thin_data_multiplier;
      coverageBand = 'thin';
    } else if (normCov >= cov.high_threshold) {
      multiplier = bucketMult;
      coverageBand = 'full';
    } else {
      const frac = (normCov - cov.low_threshold) / (cov.high_threshold - cov.low_threshold);
      multiplier = cov.thin_data_multiplier + (bucketMult - cov.thin_data_multiplier) * frac;
      coverageBand = 'blended';
    }

    return {
      parts, base, weightSum,
      hitA1y, multiplier, multiplierBand,
      coverage1y: cov1y,
      normalisedCoverage: normCov,
      windowDays,
      coverage1m: c.coverage_1m,
      coverage6m: c.coverage_6m,
      coverageBand,
      bucketMult,
      final: base * multiplier,
      usingLiveConfig: scoringConfig != null,
    };
  };

  const renderScoreTooltip = (c) => {
    const b = computeScoreBreakdown(c);
    if (!b) return <span>No score (insufficient data)</span>;
    return (
      <div style={{ fontSize: '0.85rem', lineHeight: 1.5, minWidth: 320 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Composite score breakdown</div>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #555' }}>
              <th style={{ textAlign: 'left', padding: '2px 6px 4px 0' }}>Component</th>
              <th style={{ textAlign: 'right', padding: '2px 6px 4px 0' }}>Max</th>
              <th style={{ textAlign: 'right', padding: '2px 0 4px 6px' }}>Got</th>
            </tr>
          </thead>
          <tbody>
            {b.parts.map((p, i) => (
              <tr key={i}>
                <td style={{ padding: '2px 6px 2px 0' }}>{p.label}</td>
                <td style={{ textAlign: 'right', padding: '2px 6px 2px 0' }}>{p.weight}</td>
                <td style={{ textAlign: 'right', padding: '2px 0 2px 6px' }}>{p.contrib.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid #555' }}>
              <td style={{ padding: '4px 6px 2px 0', fontWeight: 600 }}>Base score</td>
              <td style={{ textAlign: 'right', padding: '4px 6px 2px 0' }}>{b.weightSum}</td>
              <td style={{ textAlign: 'right', padding: '4px 0 2px 6px', fontWeight: 600 }}>{b.base.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: 8, fontSize: '0.8rem' }}>
          <strong>Long-horizon multiplier (DAS-2424 + DAS-2437 window-aware):</strong>
          <div style={{ marginTop: 4 }}>
            hit-α S&P 1y ={' '}
            {b.hitA1y == null
              ? 'NULL → thin-data multiplier'
              : `${b.hitA1y.toFixed(1)}% (${b.multiplierBand === 'consistent-beat' ? 'consistent beat → ×' + b.bucketMult.toFixed(2)
                  : b.multiplierBand === 'toss-up' ? 'toss-up → ×' + b.bucketMult.toFixed(2)
                  : 'loses to market → ×' + b.bucketMult.toFixed(2)})`}
          </div>
          <div style={{ marginTop: 4 }}>
            Coverage 1y ={' '}
            <strong style={{
              color: b.coverageBand === 'thin' || b.coverageBand === 'window-too-short' ? '#e74c3c'
                   : b.coverageBand === 'blended' ? '#e6a700' : '#4a8',
            }}>
              {b.coverage1y == null ? 'NULL' : `${(Number(b.coverage1y) * 100).toFixed(0)}%`}
            </strong>
            {b.windowDays != null && b.normalisedCoverage !== Number(b.coverage1y) && (
              <span style={{ color: '#aaa', fontSize: '0.85em' }}>
                {' '}(window {b.windowDays}d → normalised {(b.normalisedCoverage * 100).toFixed(0)}%)
              </span>
            )}
            {' '}
            ({b.coverageBand === 'window-too-short'
              ? <>window &lt;= 365d → no fire can have 1Y data → ×{b.multiplier.toFixed(2)}</>
              : b.coverageBand === 'thin'
                ? <>&lt; 30% → thin-data ×{b.multiplier.toFixed(2)}</>
                : b.coverageBand === 'full'
                  ? <>≥ 70% → bucket multiplier above</>
                  : <>30-70% → linear blend toward ×0.5</>})
          </div>
          <div style={{ marginTop: 4 }}>
            → effective multiplier: <strong>×{b.multiplier.toFixed(3)}</strong>
          </div>
        </div>
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #555', fontWeight: 600 }}>
          Final: {b.base.toFixed(2)} × {b.multiplier.toFixed(3)} = {b.final.toFixed(2)}
        </div>
        <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#aaa' }}>
          Formula: weighted blend over your configured components + coverage-aware
          multiplier (DAS-2424 + DAS-2437 window-aware). Tune via <em>Configure Scoring</em>.
          {!b.usingLiveConfig && (
            <span style={{ color: '#e74c3c' }}>
              {' '}⚠ Showing DEFAULTS (live config fetch failed — reload page).
            </span>
          )}
        </div>
      </div>
    );
  };

  const getScoreColor = (score) => {
    if (score == null) return '';
    if (score >= 70) return 'score-excellent';
    if (score >= 50) return 'score-good';
    if (score >= 30) return 'score-fair';
    return 'score-poor';
  };

  const getStatusBadge = (status) => {
    const colors = {
      proposed: 'badge-proposed',
      approved_for_backtest: 'badge-approved',
      backtesting: 'badge-running',
      backtested: 'badge-backtested',
      promoted: 'badge-promoted',
      rejected: 'badge-rejected',
    };
    return <span className={`status-badge ${colors[status] || ''}`}>{status}</span>;
  };

  // DAS-2415: render a regime-verdict chip next to the status badge.
  // Categories defined in alert_discovery/regime_classifier.py.
  const renderRegimeVerdict = (c) => {
    const v = c.regime_verdict;
    if (!v) return null;
    let icon = '⚪', color = '#888', label = v, tooltip = '';
    if (v.startsWith('regime_bound_bursty_')) {
      icon = '⚡'; color = '#e6a700';
      label = `${v.replace('regime_bound_bursty_', '')}-bound (bursty)`;
      tooltip = 'Strong signal during this regime, but fires in short bursts. ' +
                'Promote with the regime gate — expect episodic firing.';
    } else if (v.startsWith('regime_bound_')) {
      icon = '🎯'; color = '#27ae60';
      label = `${v.replace('regime_bound_', '')}-bound`;
      tooltip = 'Clean regime-bound signal: score jumps and hit-α exceeds 55% ' +
                'when restricted to this regime. Recommended to promote with ' +
                'the regime gate.';
    } else if (v.startsWith('implicitly_gated_')) {
      icon = '🔒'; color = '#888';
      label = `implicit ${v.replace('implicitly_gated_', '')}`;
      tooltip = "Candidate's existing clauses already select for this regime " +
                '(e.g. VIX>25 implies BEAR). Explicit regime gate would be ' +
                'redundant. Promote normally.';
    } else if (v === 'false_promise') {
      icon = '❌'; color = '#e74c3c';
      label = 'false promise';
      tooltip = 'ALL-row metrics look decent but no regime carries the signal — ' +
                'typically a few outlier wins propping up a mediocre baseline. ' +
                'Not recommended for promotion.';
    } else if (v === 'no_regime_dominance') {
      icon = '⚪'; color = '#888';
      label = 'universal';
      tooltip = 'Signal is roughly equal across regimes. Promote normally if the ' +
                'composite score warrants.';
    } else if (v === 'insufficient_data') {
      icon = '⏳'; color = '#888';
      label = 'too thin';
      tooltip = 'Best regime has fewer than 10 fires. Refine the candidate (7-day ' +
                'sampling) to get a tighter read.';
    }
    return (
      <Tooltip arrow placement="right" title={tooltip}>
        <span style={{
          display: 'inline-block',
          marginLeft: 6,
          padding: '0 6px',
          borderRadius: 3,
          fontSize: '0.75rem',
          color: color,
          border: `1px solid ${color}`,
          cursor: 'help',
        }}>
          {icon} {label}
        </span>
      </Tooltip>
    );
  };

  return (
    <div className="alert-discovery">
      <div className="ad-header">
        <h2>Alert Discovery</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="ad-btn ad-btn-primary"
            onClick={startDiscoveryRun}
            disabled={generating || pipelineProgress !== null}
          >
            {pipelineProgress
              ? (pipelineProgress.phase === 'generating'
                  ? 'Generating…'
                  : pipelineProgress.phase === 'backtesting'
                    ? `Backtesting ${pipelineProgress.done}/${pipelineProgress.total}…`
                    : pipelineProgress.phase === 'refining'
                      ? `Refining ${pipelineProgress.done}/${pipelineProgress.total}…`
                      : pipelineProgress.phase === 'proposing'
                        ? 'Proposing…'
                        : 'Done')
              : 'New Discovery Run'}
          </button>
          <button
            className="ad-btn"
            onClick={() => setShowAutonomousModal(true)}
            title="DAS-2420: start the overnight autonomous loop"
            disabled={autonomousStarted !== null}
          >
            {autonomousStarted ? `Autonomous running (PID ${autonomousStarted.pid})` : 'Start Autonomous Run'}
          </button>
          <button
            className="ad-btn"
            onClick={openScoringModal}
            title="DAS-2418: tune composite-score formula before generating a run"
          >
            Configure Scoring
          </button>
          {/* DAS-2460: archetype editor */}
          <button
            className="ad-btn"
            onClick={openArchetypesModal}
            title="DAS-2460: edit which archetype categories are active, what clauses they include, and what theme the LLM sees."
          >
            Configure Archetypes
          </button>
        </div>
      </div>

      {/* DAS-2460: archetype-editor modal */}
      {showArchetypesModal && (
        <div className="ad-modal-overlay" onClick={() => setShowArchetypesModal(false)}>
          <div
            className="ad-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900, maxHeight: '85vh', overflowY: 'auto' }}
          >
            <h3>Configure Archetypes</h3>
            <p style={{ fontSize: '0.85em', opacity: 0.75 }}>
              These archetypes drive the parallel slots in each Discovery Run
              (DAS-2431). Each slot is anchored to one archetype's clause fields;
              the LLM then completes the candidate using anything from the full
              catalog. <em>Disabled</em> archetypes are skipped entirely.
              <code>free_choice</code> has no clause restriction and cannot be deleted.
            </p>
            {archetypesError && (
              <div style={{
                color: '#fff',
                background: 'rgba(192, 57, 43, 0.85)',
                padding: '8px 12px',
                borderRadius: 4,
                margin: '8px 0',
                fontFamily: 'monospace',
                fontSize: '0.85em',
              }}>
                Error: {archetypesError}
              </div>
            )}
            {!archetypesError && archetypes.length === 0 && (
              <div style={{
                padding: '24px 12px',
                textAlign: 'center',
                opacity: 0.7,
                fontStyle: 'italic',
              }}>
                Loading archetypes from <code>alert_discovery_archetypes</code>…
                <br />
                <span style={{ fontSize: '0.8em' }}>
                  (If this persists, hard-refresh the page or check the browser
                  Network tab for the <code>/archetypes</code> request.)
                </span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {archetypes.map(a => (
                <div
                  key={a.name}
                  style={{
                    border: '1px solid rgba(120, 170, 255, 0.25)',
                    borderRadius: 6,
                    padding: 12,
                    background: a.enabled ? 'rgba(120, 170, 255, 0.04)' : 'rgba(180, 180, 180, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={!!a.enabled}
                        onChange={() => toggleArchetypeEnabled(a.name)}
                      />
                      <strong>{a.label || a.name}</strong>
                      <code style={{ fontSize: '0.85em', opacity: 0.65 }}>{a.name}</code>
                    </label>
                    <span style={{ flex: 1 }} />
                    {a._dirty && <span style={{ fontSize: '0.8em', color: '#e67e22' }}>● unsaved</span>}
                    {a.name !== 'free_choice' && (
                      <button
                        className="ad-btn ad-btn-sm ad-btn-danger"
                        onClick={() => deleteArchetypeRow(a.name)}
                        disabled={archetypesSaving}
                        title="Delete this archetype permanently"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontSize: '0.85em', opacity: 0.75 }}>
                      Theme (shown to the LLM in every prompt for this archetype):
                    </label>
                    <textarea
                      value={a.theme || ''}
                      onChange={e => updateArchetypeField(a.name, 'theme', e.target.value)}
                      rows={3}
                      style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.9em' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85em', opacity: 0.75, marginBottom: 4 }}>
                      Clause fields ({a.clause_fields === null ? 'any (free choice)' : `${a.clause_fields.length} selected`}):
                    </label>
                    {a.clause_fields === null ? (
                      <div style={{ fontSize: '0.85em', opacity: 0.65, fontStyle: 'italic' }}>
                        This archetype matches every backtestable clause. (The
                        special "any" mode is reserved for <code>free_choice</code>.)
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {availableFields.map(f => {
                          const selected = a.clause_fields.includes(f);
                          return (
                            <button
                              key={f}
                              type="button"
                              onClick={() => toggleArchetypeClauseField(a.name, f)}
                              style={{
                                padding: '2px 8px',
                                fontSize: '0.8em',
                                borderRadius: 4,
                                border: selected
                                  ? '1px solid rgba(120, 170, 255, 0.7)'
                                  : '1px solid rgba(150, 150, 150, 0.3)',
                                background: selected
                                  ? 'rgba(120, 170, 255, 0.25)'
                                  : 'transparent',
                                color: 'inherit',
                                cursor: 'pointer',
                              }}
                              title={selected ? 'Click to remove' : 'Click to add'}
                            >
                              {f}
                            </button>
                          );
                        })}
                        {/* Show stale fields (in clause_fields but no longer in catalog) so the user knows. */}
                        {a.clause_fields.filter(f => !availableFields.includes(f)).map(f => (
                          <button
                            key={f}
                            type="button"
                            onClick={() => toggleArchetypeClauseField(a.name, f)}
                            style={{
                              padding: '2px 8px',
                              fontSize: '0.8em',
                              borderRadius: 4,
                              border: '1px dashed #c0392b',
                              background: 'rgba(192, 57, 43, 0.12)',
                              color: 'inherit',
                              cursor: 'pointer',
                            }}
                            title="This field is no longer in the backtestable catalog. Click to remove."
                          >
                            {f} ⚠
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                className="ad-btn ad-btn-sm"
                onClick={addNewArchetype}
                disabled={archetypesSaving}
              >
                + Add archetype
              </button>
              <span style={{ flex: 1 }} />
              <button
                className="ad-btn ad-btn-sm"
                onClick={() => setShowArchetypesModal(false)}
                disabled={archetypesSaving}
              >
                Cancel
              </button>
              <button
                className="ad-btn ad-btn-primary"
                onClick={saveAllArchetypes}
                disabled={archetypesSaving}
              >
                {archetypesSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DAS-2418: scoring-config modal */}
      {showScoringModal && (
        <div className="ad-modal-overlay" onClick={() => setShowScoringModal(false)}>
          <div
            className="ad-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' }}
          >
            <h3>Configure Composite Scoring</h3>
            <p style={{ fontSize: '0.85em', color: '#aaa' }}>
              These parameters drive the composite_score formula and the
              coverage-aware multiplier (DAS-2424). Changes affect every
              Discovery Run going forward. Reset to defaults at any time.
            </p>
            {scoringError && (
              <div style={{ color: '#fa8', padding: '8px 0' }}>{scoringError}</div>
            )}
            {!scoringConfig && !scoringError && (
              <div style={{ color: '#888' }}>Loading…</div>
            )}
            {scoringConfig && (
              <>
                <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '12px' }}>
                  <strong style={{ color: '#4a8' }}>Component weights</strong>
                  <small style={{ color: '#888', display: 'block', marginBottom: '6px' }}>
                    Relative weight each metric contributes to the base score (out of total).
                  </small>
                  {Object.entries(scoringConfig.weights || {}).map(([k, v]) => (
                    <div key={k} className="ad-modal-field">
                      <label>
                        {k.replace(/_/g, ' ')}
                        <input
                          type="number" step="1" min="0"
                          value={v}
                          onChange={(e) => updateScoringField('weights', k, e.target.value)}
                        />
                      </label>
                      <small style={{ color: '#666' }}>default: {scoringDefaults?.weights?.[k]}</small>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '12px' }}>
                  <strong style={{ color: '#4a8' }}>hit-α 1Y thresholds (%)</strong>
                  <small style={{ color: '#888', display: 'block', marginBottom: '6px' }}>
                    Cutoffs for the long-horizon multiplier. Above "high" = consistent-beat band.
                  </small>
                  {Object.entries(scoringConfig.hit_alpha_1y_thresholds || {}).map(([k, v]) => (
                    <div key={k} className="ad-modal-field">
                      <label>
                        {k}
                        <input
                          type="number" step="1" min="0" max="100"
                          value={v}
                          onChange={(e) => updateScoringField('hit_alpha_1y_thresholds', k, e.target.value)}
                        />
                      </label>
                      <small style={{ color: '#666' }}>default: {scoringDefaults?.hit_alpha_1y_thresholds?.[k]}</small>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '12px' }}>
                  <strong style={{ color: '#4a8' }}>Multiplier values</strong>
                  <small style={{ color: '#888', display: 'block', marginBottom: '6px' }}>
                    What the base score gets multiplied by in each band.
                  </small>
                  {Object.entries(scoringConfig.multipliers || {}).map(([k, v]) => (
                    <div key={k} className="ad-modal-field">
                      <label>
                        {k}
                        <input
                          type="number" step="0.05" min="0" max="2"
                          value={v}
                          onChange={(e) => updateScoringField('multipliers', k, e.target.value)}
                        />
                      </label>
                      <small style={{ color: '#666' }}>default: {scoringDefaults?.multipliers?.[k]}</small>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '12px' }}>
                  <strong style={{ color: '#4a8' }}>Coverage (DAS-2424)</strong>
                  <small style={{ color: '#888', display: 'block', marginBottom: '6px' }}>
                    Below low_threshold → thin-data multiplier. Above high_threshold → bucket multiplier. In between → linear blend.
                  </small>
                  {Object.entries(scoringConfig.coverage || {}).map(([k, v]) => (
                    <div key={k} className="ad-modal-field">
                      <label>
                        {k.replace(/_/g, ' ')}
                        <input
                          type="number" step="0.05" min="0" max="2"
                          value={v}
                          onChange={(e) => updateScoringField('coverage', k, e.target.value)}
                        />
                      </label>
                      <small style={{ color: '#666' }}>default: {scoringDefaults?.coverage?.[k]}</small>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '12px' }}>
                  <strong style={{ color: '#4a8' }}>Fires-per-year sweet spot</strong>
                  <small style={{ color: '#888', display: 'block', marginBottom: '6px' }}>
                    Bell-curve frequency band. Inside [lo, hi] = 1.0 weight; outside falls off.
                  </small>
                  {Object.entries(scoringConfig.fires_per_year_band || {}).map(([k, v]) => (
                    <div key={k} className="ad-modal-field">
                      <label>
                        {k}
                        <input
                          type="number" step="1" min="0"
                          value={v}
                          onChange={(e) => updateScoringField('fires_per_year_band', k, e.target.value)}
                        />
                      </label>
                      <small style={{ color: '#666' }}>default: {scoringDefaults?.fires_per_year_band?.[k]}</small>
                    </div>
                  ))}
                </div>

                <div className="ad-modal-actions" style={{ marginTop: '20px' }}>
                  <button
                    className="ad-btn ad-btn-sm"
                    onClick={resetScoringConfig}
                    disabled={scoringSaving}
                    title="Restore all values to their original defaults"
                  >
                    Reset to defaults
                  </button>
                  <button
                    className="ad-btn ad-btn-sm"
                    onClick={() => setShowScoringModal(false)}
                    disabled={scoringSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="ad-btn ad-btn-primary"
                    onClick={saveScoringConfig}
                    disabled={scoringSaving}
                  >
                    {scoringSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* DAS-2420: Autonomous-run config modal */}
      {/* DAS-2430: amendment modal */}
      {amendModalOpen && (
        <div className="ad-modal-overlay" onClick={() => !amendLoading && setAmendModalOpen(false)}>
          <div className="ad-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <h3>Propose clause-tuning amendments — candidate #{amendParentId}</h3>
            <p style={{ fontSize: '0.85em', color: '#aaa' }}>
              The LLM proposes small tweaks (tighten/loosen threshold, swap to a sibling clause in
              the same clause_field, or add one extra clause from the catalog). Each accepted variant
              becomes a new candidate row linked to #{amendParentId}, queued for backtest. Variants
              flow through the same should_propose gates as their parent — no safety hole.
            </p>
            {amendLoading && <p style={{ color: '#aaa' }}>Calling LLM (~10s)…</p>}
            {amendError && (
              <div style={{ background: 'rgba(231,76,60,0.18)', color: '#e74c3c', padding: 8, borderRadius: 4, marginBottom: 8 }}>
                {amendError}
              </div>
            )}
            {!amendLoading && amendProposals.length > 0 && (
              <div>
                <table className="ad-table" style={{ width: '100%', fontSize: '0.88em' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>Archetype</th>
                      <th>Target / Swap / Add</th>
                      <th>Rationale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amendProposals.map((a, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            type="checkbox"
                            checked={amendSelected.has(i)}
                            onChange={() => {
                              setAmendSelected(prev => {
                                const next = new Set(prev);
                                if (next.has(i)) next.delete(i); else next.add(i);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td><code>{a.archetype}</code></td>
                        <td style={{ fontSize: '0.9em' }}>
                          {a.target_clause_id != null && <>target: #{a.target_clause_id}</>}
                          {a.new_threshold != null && <> → <strong>{String(a.new_threshold)}</strong></>}
                          {a.swap_to_clause_id != null && <>; swap to #{a.swap_to_clause_id}</>}
                          {a.add_clause_id != null && <>add: #{a.add_clause_id}</>}
                        </td>
                        <td style={{ color: '#bbb' }}>{a.rationale || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="ad-btn ad-btn-sm" onClick={() => setAmendModalOpen(false)}>Cancel</button>
                  <button
                    className="ad-btn ad-btn-primary"
                    onClick={spawnSelectedAmendments}
                    disabled={amendLoading || amendSelected.size === 0}
                  >
                    Spawn {amendSelected.size} selected
                  </button>
                </div>
              </div>
            )}
            {!amendLoading && amendProposals.length === 0 && !amendError && (
              <p style={{ color: '#aaa' }}>No proposals returned.</p>
            )}
          </div>
        </div>
      )}

      {showAutonomousModal && (
        <div className="ad-modal-overlay" onClick={() => setShowAutonomousModal(false)}>
          <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Start Autonomous Discovery</h3>
            <p style={{ fontSize: '0.85em', color: '#aaa' }}>
              Spawns the overnight loop as a background process. It generates,
              backtests, refines (rule-based), and writes morning proposals
              until the budget or cutoff hour is hit.
            </p>
            <div className="ad-modal-field">
              <label>
                Budget (USD)
                <input
                  type="number"
                  min="0.5"
                  max="50"
                  step="0.5"
                  value={autoBudgetUsd}
                  onChange={(e) => setAutoBudgetUsd(e.target.value)}
                />
              </label>
              <small>Hard cap on estimated Anthropic spend. Default $5.</small>
            </div>
            <div className="ad-modal-field">
              <label>
                Cutoff hour (24h clock)
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={autoCutoffHour}
                  onChange={(e) => setAutoCutoffHour(e.target.value)}
                />
              </label>
              <small>Loop stops when local time reaches this hour. Default 5 (= 05:00).</small>
            </div>
            <div className="ad-modal-field">
              <label>
                Candidates per run
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={autoCandidatesPerRun}
                  onChange={(e) => setAutoCandidatesPerRun(e.target.value)}
                />
              </label>
              <small>Smaller = more iterations, more variety. Default 10.</small>
            </div>
            {/* DAS-2446: exemplar mode for the loop */}
            <div className="ad-modal-field">
              <label>
                Exemplar mode (DAS-2443 / 2446)
                <select
                  value={autoExemplarMode}
                  onChange={(e) => setAutoExemplarMode(e.target.value)}
                >
                  <option value="off">Off — no exemplar block (baseline)</option>
                  <option value="way1">Way 1 — anchor on top live alerts every iteration</option>
                  <option value="way2">Way 2 — winners-vs-losers contrast every iteration</option>
                  <option value="shuffle">Shuffle — random per iteration (off / way1 / way2)</option>
                </select>
              </label>
              <small>
                <strong>Off</strong>: pure DAS-2431 archetype-quota generation.
                <strong> Way 1</strong>: lower variance, anchored exploit.
                <strong> Way 2</strong>: wider exploration, requires LLM to reason about the contrast.
                <strong> Shuffle</strong>: variety across the night — best when you don't know which mode wins.
                The selected mode is logged per iteration as <code>[iter N] exemplar mode: X</code>.
              </small>
            </div>
            <div className="ad-modal-actions">
              <button className="ad-btn ad-btn-sm" onClick={() => setShowAutonomousModal(false)}>
                Cancel
              </button>
              <button className="ad-btn ad-btn-primary" onClick={startAutonomousDiscovery}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DAS-2420: Promotion proposals panel — ALWAYS visible so the user
          can see system state (loading / error / empty / N pending).
          Sits above Discovery Runs with a coloured border so it can't be
          scrolled past silently. */}
      <div
        className="ad-runs"
        style={{
          border: `2px solid ${proposalsError ? '#c66' : proposals.length > 0 ? '#4a8' : '#444'}`,
          borderRadius: '6px',
          marginBottom: '20px',
          padding: '0',
          background: proposals.length > 0 ? 'rgba(74, 170, 136, 0.05)' : 'transparent',
        }}
      >
        <div
          className="ad-candidates-header"
          style={{ padding: '12px 16px', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setProposalsExpanded(v => !v)}
        >
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#888', fontSize: '0.85em' }}>
              {proposalsExpanded ? '▼' : '▶'}
            </span>
            Promotion Proposals
            <span
              style={{
                background: proposals.length > 0 ? '#4a8' : '#666',
                color: '#fff',
                fontSize: '0.7em',
                padding: '2px 10px',
                borderRadius: '12px',
                fontWeight: 'bold',
              }}
            >
              {proposals.length} pending
            </span>
            {proposalsLoading && (
              <span style={{ fontSize: '0.75em', color: '#888' }}>refreshing…</span>
            )}
          </h3>
          <div
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            {proposalsLastLoaded && (
              <small style={{ color: '#888' }}>
                last loaded {proposalsLastLoaded.toLocaleTimeString()}
              </small>
            )}
            <button className="ad-btn ad-btn-sm" onClick={fetchProposals}>
              Refresh
            </button>
          </div>
        </div>

        {proposalsExpanded && proposalsError && (
          <div
            style={{
              padding: '12px 16px',
              background: '#3a1010',
              color: '#fcc',
              fontSize: '0.9em',
              borderTop: '1px solid #c66',
            }}
          >
            <strong>Error:</strong> {proposalsError}
            <br />
            <small style={{ color: '#fa8' }}>
              If this says "401" or "Unauthorized", your JWT token expired —
              log out and back in.
            </small>
          </div>
        )}

        {proposalsExpanded && !proposalsError && proposals.length === 0 && (
          <div
            style={{
              padding: '20px 16px',
              color: '#888',
              fontStyle: 'italic',
              textAlign: 'center',
              borderTop: '1px solid #333',
            }}
          >
            No pending proposals.
            {' '}
            {proposalsLastLoaded
              ? 'The autonomous loop will populate this when candidates pass the propose threshold.'
              : 'Click Refresh, or wait — the panel auto-polls every 60s.'}
          </div>
        )}

        {proposalsExpanded && !proposalsError && proposals.length > 0 && (
          <table className="ad-table" style={{ marginTop: 0 }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Candidate</th>
                <th>Type</th>
                <th>Verdict</th>
                <th>Score</th>
                <th>Suggested gates</th>
                <th>Reasoning</th>
                <th>Proposed at</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {proposals.flatMap(p => {
                const secOp = p.suggested_sector_gate_op || '=';
                const sectorGateStr = p.suggested_sector_gate
                  ? (secOp === '!='
                      ? `sector ≠ ${p.suggested_sector_gate}`
                      : `sector = ${p.suggested_sector_gate}`)
                  : null;
                const gates = [
                  p.suggested_regime_gate && `regime: ${p.suggested_regime_gate}`,
                  sectorGateStr,
                ].filter(Boolean).join(' + ') || 'ungated';
                const isOpen = expandedProposalId === p.id;
                const details = proposalDetails[p.candidate_id];
                const rows = [
                  <tr
                    key={p.id}
                    onClick={() => toggleProposalExpansion(p)}
                    style={{
                      cursor: 'pointer',
                      background: isOpen ? 'rgba(74, 170, 136, 0.12)' : undefined,
                    }}
                    title="Click to expand details"
                  >
                    <td>
                      <span style={{ marginRight: '6px', color: '#888' }}>
                        {isOpen ? '▼' : '▶'}
                      </span>
                      #{p.id}
                    </td>
                    <td>#{p.candidate_id} (run {p.run_id})</td>
                    <td>{p.alert_type}<br /><small style={{ color: '#888' }}>{p.alert_subtype}</small></td>
                    <td>
                      {p.regime_verdict ? (
                        <Tooltip
                          title={VERDICT_TOOLTIPS[p.regime_verdict] || 'Unknown verdict label'}
                          arrow
                        >
                          <small style={{ cursor: 'help', borderBottom: '1px dotted #888' }}>
                            {p.regime_verdict}
                          </small>
                        </Tooltip>
                      ) : <small>—</small>}
                    </td>
                    <td>
                      {fmtNum(p.suggested_composite_score, 1)}
                      {p.composite_score != null && p.suggested_composite_score != null &&
                       Math.abs(Number(p.composite_score) - Number(p.suggested_composite_score)) >= 1 && (
                        <Tooltip
                          title={`Snapshot score at proposal time was ${fmtNum(p.suggested_composite_score, 1)}; live score is now ${fmtNum(p.composite_score, 1)} (DAS-2424 score recompute or refine changed it).`}
                          arrow
                        >
                          <span style={{ display: 'block', fontSize: '0.75em', color: Number(p.composite_score) < Number(p.suggested_composite_score) ? '#e74c3c' : '#4a8' }}>
                            now {fmtNum(p.composite_score, 1)}
                          </span>
                        </Tooltip>
                      )}
                    </td>
                    <td><strong>{gates}</strong></td>
                    <td style={{ maxWidth: '300px', fontSize: '0.85em', color: '#aaa' }}>
                      {p.reasoning_txt}
                    </td>
                    <td><small>{new Date(p.proposed_at).toLocaleString()}</small></td>
                    <td
                      style={{ display: 'flex', gap: '4px' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="ad-btn ad-btn-sm ad-btn-primary"
                        onClick={() => approveProposal(p)}
                        disabled={reviewingId === p.id}
                      >
                        Approve
                      </button>
                      <button
                        className="ad-btn ad-btn-sm"
                        onClick={() => dismissProposal(p)}
                        disabled={reviewingId === p.id}
                      >
                        Dismiss
                      </button>
                    </td>
                  </tr>,
                ];
                if (isOpen) {
                  rows.push(
                    <tr key={`${p.id}-detail`}>
                      <td colSpan={9} style={{ padding: '16px 20px', background: '#0d1418' }}>
                        {proposalDetailsLoading && !details && (
                          <div style={{ color: '#888' }}>Loading details…</div>
                        )}
                        {details && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {/* Clauses block */}
                            <div>
                              <strong style={{ color: '#4a8' }}>Clauses</strong>
                              <ul style={{ marginTop: '6px', paddingLeft: '20px', color: '#ccc' }}>
                                {(() => {
                                  const ids = Array.isArray(p.clauses_json)
                                    ? p.clauses_json
                                    : (typeof p.clauses_json === 'string'
                                      ? JSON.parse(p.clauses_json || '[]')
                                      : []);
                                  return ids.map(cid => (
                                    <li key={cid}>
                                      <code style={{ color: '#888' }}>#{cid}</code>
                                      {' — '}
                                      {clauseTexts[cid] || <em style={{ color: '#666' }}>(text not loaded)</em>}
                                    </li>
                                  ));
                                })()}
                              </ul>
                            </div>

                            {/* Regime slice */}
                            <div>
                              <strong style={{ color: '#4a8' }}>Regime slice</strong>
                              {details.regime.length === 0 ? (
                                <div style={{ color: '#888', fontStyle: 'italic', marginTop: '4px' }}>
                                  No regime slice computed.
                                </div>
                              ) : (
                                <table
                                  className="ad-table"
                                  style={{ marginTop: '6px', fontSize: '0.85em' }}
                                >
                                  <thead>
                                    <tr>
                                      <th>Regime</th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.fires} arrow><span style={{ cursor: 'help' }}>Fires</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.fpy} arrow><span style={{ cursor: 'help' }}>FPY</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.score} arrow><span style={{ cursor: 'help' }}>Score</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.hit1m} arrow><span style={{ cursor: 'help' }}>Hit 1M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.avgA6m} arrow><span style={{ cursor: 'help' }}>Avg α 6M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.medA6m} arrow><span style={{ cursor: 'help' }}>Med α 6M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.hitA6m} arrow><span style={{ cursor: 'help' }}>Hit α 6M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.avgA1y} arrow><span style={{ cursor: 'help' }}>Avg α 1Y</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.medA1y} arrow><span style={{ cursor: 'help' }}>Med α 1Y</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.hitA1y} arrow><span style={{ cursor: 'help' }}>Hit α 1Y</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.tc} arrow><span style={{ cursor: 'help' }}>TC</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.tickers} arrow><span style={{ cursor: 'help' }}>#TKR</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.tickerConc} arrow><span style={{ cursor: 'help' }}>TConc</span></Tooltip></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.regime.map(r => (
                                      <tr
                                        key={r.regime}
                                        style={{
                                          fontWeight: r.regime === p.suggested_regime_gate ? 'bold' : 'normal',
                                          color: r.regime === p.suggested_regime_gate ? '#4a8' : undefined,
                                        }}
                                      >
                                        <td>{r.regime}{r.regime === p.suggested_regime_gate && ' ←'}</td>
                                        <td>{r.total_fires}</td>
                                        <td>{fmtNum(r.fires_per_year, 1)}</td>
                                        <td>{fmtNum(r.composite_score, 1)}</td>
                                        <td>{fmtNum(r.hit_rate_1m, 1, '%')}</td>
                                        <td>{fmtNum(r.avg_alpha_sp500_6m, 2, '%')}</td>
                                        <td>{fmtNum(r.median_alpha_sp500_6m, 2, '%')}</td>
                                        <td>{fmtNum(r.hit_rate_alpha_sp500_6m, 1, '%')}</td>
                                        <td>{fmtNum(r.avg_alpha_sp500_1y, 2, '%')}</td>
                                        <td>{fmtNum(r.median_alpha_sp500_1y, 2, '%')}</td>
                                        <td>{fmtNum(r.hit_rate_alpha_sp500_1y, 1, '%')}</td>
                                        <td>{r.temporal_concentration != null && r.temporal_concentration !== '' ? `${(Number(r.temporal_concentration) * 100).toFixed(0)}%` : '—'}</td>
                                        <td style={{
                                          color: r.unique_tickers != null && Number(r.unique_tickers) < 5 ? '#e74c3c' : undefined,
                                          fontWeight: r.unique_tickers != null && Number(r.unique_tickers) < 5 ? 'bold' : undefined,
                                        }}>
                                          {r.unique_tickers ?? '—'}
                                        </td>
                                        <td style={{
                                          color: r.ticker_concentration != null && Number(r.ticker_concentration) > 0.6 ? '#e74c3c' : undefined,
                                        }}>
                                          {fmtNum(r.ticker_concentration, 3)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>

                            {/* Sector slice */}
                            <div>
                              <strong style={{ color: '#4a8' }}>Sector slice</strong>
                              {details.sector.length === 0 ? (
                                <div style={{ color: '#888', fontStyle: 'italic', marginTop: '4px' }}>
                                  No sector slice computed.
                                </div>
                              ) : (
                                <table
                                  className="ad-table"
                                  style={{ marginTop: '6px', fontSize: '0.85em' }}
                                >
                                  <thead>
                                    <tr>
                                      <th>Sector</th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.fires} arrow><span style={{ cursor: 'help' }}>Fires</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.fpy} arrow><span style={{ cursor: 'help' }}>FPY</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.score} arrow><span style={{ cursor: 'help' }}>Score</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.hit1m} arrow><span style={{ cursor: 'help' }}>Hit 1M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.avgA6m} arrow><span style={{ cursor: 'help' }}>Avg α 6M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.medA6m} arrow><span style={{ cursor: 'help' }}>Med α 6M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.hitA6m} arrow><span style={{ cursor: 'help' }}>Hit α 6M</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.avgA1y} arrow><span style={{ cursor: 'help' }}>Avg α 1Y</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.medA1y} arrow><span style={{ cursor: 'help' }}>Med α 1Y</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.hitA1y} arrow><span style={{ cursor: 'help' }}>Hit α 1Y</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.tc} arrow><span style={{ cursor: 'help' }}>TC</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.tickers} arrow><span style={{ cursor: 'help' }}>#TKR</span></Tooltip></th>
                                      <th><Tooltip title={SLICE_COL_TOOLTIPS.tickerConc} arrow><span style={{ cursor: 'help' }}>TConc</span></Tooltip></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.sector.map(r => {
                                      const isGated = r.sector === p.suggested_sector_gate;
                                      const isExclude = (p.suggested_sector_gate_op || '=') === '!=';
                                      const highlightColor = !isGated ? undefined
                                        : isExclude ? '#e74c3c'   // red = excluded
                                                    : '#4a8';      // green = included
                                      const arrow = !isGated ? '' : (isExclude ? ' ✕' : ' ←');
                                      return (
                                      <tr
                                        key={r.sector}
                                        style={{
                                          fontWeight: isGated ? 'bold' : 'normal',
                                          color: highlightColor,
                                        }}
                                      >
                                        <td>{r.sector}{arrow}</td>
                                        <td>{r.total_fires}</td>
                                        <td>{fmtNum(r.fires_per_year, 1)}</td>
                                        <td>{fmtNum(r.composite_score, 1)}</td>
                                        <td>{fmtNum(r.hit_rate_1m, 1, '%')}</td>
                                        <td>{fmtNum(r.avg_alpha_sp500_6m, 2, '%')}</td>
                                        <td>{fmtNum(r.median_alpha_sp500_6m, 2, '%')}</td>
                                        <td>{fmtNum(r.hit_rate_alpha_sp500_6m, 1, '%')}</td>
                                        <td>{fmtNum(r.avg_alpha_sp500_1y, 2, '%')}</td>
                                        <td>{fmtNum(r.median_alpha_sp500_1y, 2, '%')}</td>
                                        <td>{fmtNum(r.hit_rate_alpha_sp500_1y, 1, '%')}</td>
                                        <td>{r.temporal_concentration != null && r.temporal_concentration !== '' ? `${(Number(r.temporal_concentration) * 100).toFixed(0)}%` : '—'}</td>
                                        <td style={{
                                          color: r.unique_tickers != null && Number(r.unique_tickers) < 5 ? '#e74c3c' : undefined,
                                          fontWeight: r.unique_tickers != null && Number(r.unique_tickers) < 5 ? 'bold' : undefined,
                                        }}>
                                          {r.unique_tickers ?? '—'}
                                        </td>
                                        <td style={{
                                          color: r.ticker_concentration != null && Number(r.ticker_concentration) > 0.6 ? '#e74c3c' : undefined,
                                        }}>
                                          {fmtNum(r.ticker_concentration, 3)}
                                        </td>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>

                            {/* Meta block */}
                            <div style={{ display: 'flex', gap: '24px', fontSize: '0.85em', color: '#aaa' }}>
                              <span>Composite (overall): <strong>{fmtNum(p.composite_score, 1)}</strong></span>
                              <span>Total fires: <strong>{p.total_fires ?? '—'}</strong></span>
                              <span>Refined: <strong>{p.is_refined ? 'yes' : 'no'}</strong></span>
                              <span>Proposed at: <strong>{new Date(p.proposed_at).toLocaleString()}</strong></span>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                }
                return rows;
              })}
            </tbody>
          </table>
        )}
      </div>

      {showRunModal && (
        <div className="ad-modal-overlay" onClick={() => setShowRunModal(false)}>
          <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
            <h3>New Discovery Run</h3>
            <div className="ad-modal-field">
              <label>
                Number of candidates
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={runNumCandidates}
                  onChange={(e) => setRunNumCandidates(e.target.value)}
                />
              </label>
              <small>Integer 1–50. Default 20.</small>
            </div>
            <div className="ad-modal-field">
              <label>
                <input
                  type="checkbox"
                  checked={runAutoPipeline}
                  onChange={(e) => setRunAutoPipeline(e.target.checked)}
                />
                Auto-pipeline: backtest all + refine every candidate that fires
              </label>
              <small>
                Generates candidates, then backtests all, then refines (7-day sampling)
                every candidate with ≥1 fire. Adds ~2 min per fired candidate. Errors
                in any step are non-fatal — the pipeline continues and a summary is
                shown at the end.
              </small>
            </div>
            {/* DAS-2443: live-alert exemplar toggles */}
            <div className="ad-modal-field">
              <label>
                <input
                  type="checkbox"
                  checked={runWay1}
                  onChange={(e) => setRunWay1(e.target.checked)}
                />
                Way 1 — anchor on top live alerts (DAS-2443)
              </label>
              <small>
                Injects the top-5 best-performing live alerts (by α-vs-S&P over last 12mo,
                min 30 fires) into the LLM prompt as exemplars. LLM produces candidates
                resembling proven winners. Lower variance, higher yield, narrower exploration.
              </small>
            </div>
            <div className="ad-modal-field">
              <label>
                <input
                  type="checkbox"
                  checked={runWay2}
                  onChange={(e) => setRunWay2(e.target.checked)}
                />
                Way 2 — winners-vs-losers contrast (DAS-2443)
              </label>
              <small>
                Injects BOTH top-5 winners AND bottom-5 losers. LLM extracts what
                distinguishes them and proposes candidates that follow the winning pattern
                with novel clause IDs. Wider exploration than Way 1, requires reasoning to land.
              </small>
            </div>
            <div className="ad-modal-actions">
              <button className="ad-btn ad-btn-sm" onClick={() => setShowRunModal(false)}>
                Cancel
              </button>
              <button className="ad-btn ad-btn-primary" onClick={executeDiscoveryRun}>
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="ad-error">{error}<button onClick={() => setError(null)}>x</button></div>}

      <div className="ad-runs">
        <div
          className="ad-candidates-header"
          style={{ cursor: 'pointer' }}
          onClick={() => setRunsExpanded(v => !v)}
        >
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#888', fontSize: '0.85em' }}>
              {runsExpanded ? '▼' : '▶'}
            </span>
            Discovery Runs
            <span style={{ fontSize: '0.75em', color: '#888', fontWeight: 'normal' }}>
              ({runs.length})
            </span>
          </h3>
          <div
            className="ad-candidates-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {/* DAS-2458: global candidate-ID lookup across ALL runs. */}
            <input
              type="text"
              value={globalCandidateLookup}
              onChange={e => setGlobalCandidateLookup(e.target.value.trim())}
              onKeyDown={e => {
                if (e.key === 'Enter') locateCandidate();
              }}
              placeholder="Find candidate #ID…"
              title="Type a candidate ID (e.g. 960) and press Enter or click Locate. Switches to that candidate's run and opens it. Works across all runs, not just the open one."
              style={{
                padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc',
                width: 130, fontVariantNumeric: 'tabular-nums',
              }}
            />
            <button
              className="ad-btn ad-btn-sm"
              onClick={locateCandidate}
              disabled={!globalCandidateLookup || locatingCandidate}
              title="Look up the candidate ID and jump to its run"
            >
              {locatingCandidate ? 'Locating…' : 'Locate'}
            </button>
            {locateError && (
              <span style={{ color: '#c0392b', fontSize: '0.85em' }}>{locateError}</span>
            )}
            {/* DAS-2458: cross-run all-candidates view toggle. */}
            <button
              className={`ad-btn ad-btn-sm ${viewMode === 'all' ? 'ad-btn-primary' : ''}`}
              onClick={() => {
                if (viewMode === 'all') {
                  setViewMode('run');
                } else {
                  setViewMode('all');
                  if (!allCandidates.length) fetchAllCandidates();
                }
              }}
              title="DAS-2458: toggle a cross-run table of EVERY candidate, sortable by any column. Useful when you don't know which run a candidate belongs to or want to compare across runs."
            >
              {viewMode === 'all' ? 'Back to per-run view' : 'View ALL candidates'}
            </button>
            {viewMode === 'all' && (
              <button
                className="ad-btn ad-btn-sm"
                onClick={fetchAllCandidates}
                disabled={loadingAll}
                title="Re-fetch all candidates (after a recompute or new backtest)"
              >
                {loadingAll ? 'Loading…' : 'Reload'}
              </button>
            )}
            <button
              className="ad-btn ad-btn-sm ad-btn-danger"
              onClick={bulkDeleteRuns}
              disabled={selectedRuns.size === 0}
            >
              Delete Selected ({selectedRuns.size})
            </button>
          </div>
        </div>
        {runsExpanded && (
        <table className="ad-table ad-runs-table">
          <thead>
            <tr>
              <th className="col-check">
                <input
                  type="checkbox"
                  checked={runs.length > 0 && selectedRuns.size === runs.length}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedRuns(new Set(runs.map(r => r.id)));
                    else setSelectedRuns(new Set());
                  }}
                  title="Select all"
                />
              </th>
              <th>ID</th>
              <th>Status</th>
              <th>Model</th>
              <th>Candidates</th>
              <th title="DAS-2447: actual LLM cost for this run (sum of input + cache-write + cache-read + output). Hover for token breakdown.">Cost</th>
              <th>Started</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {runs.map(run => {
              // DAS-2447: cache-hit ratio = cache_reads / (cache_reads + cache_writes).
              // A healthy 10-slot run is ≥0.85 (1 write + 9 reads).
              const cr = run.cache_read_tokens || 0;
              const cw = run.cache_creation_tokens || 0;
              const hitRatio = (cr + cw) > 0 ? (cr / (cr + cw)) : null;
              const costTooltip = run.actual_cost_usd != null
                ? `in=${run.input_tokens ?? 0}  out=${run.output_tokens ?? 0}\n`
                  + `cache_write=${cw}  cache_read=${cr}\n`
                  + `api_calls=${run.llm_api_calls ?? 0}`
                  + (hitRatio != null ? `\ncache_hit_ratio=${(hitRatio * 100).toFixed(1)}%` : '')
                : 'No cost data (pre-DAS-2447 run, or run failed before LLM call)';
              return (
              <tr key={run.id} className={selectedRunId === run.id ? 'selected' : ''}>
                <td className="col-check">
                  <input
                    type="checkbox"
                    checked={selectedRuns.has(run.id)}
                    onChange={() => toggleRunSelection(run.id)}
                  />
                </td>
                <td>{run.id}</td>
                <td>{getStatusBadge(run.status)}</td>
                <td>{run.llm_model}</td>
                <td>{run.candidates_generated ?? run.candidates_requested}</td>
                <td title={costTooltip} style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {run.actual_cost_usd != null
                    ? `$${run.actual_cost_usd.toFixed(4)}${hitRatio != null && hitRatio >= 0.5 ? ' 💾' : ''}`
                    : '—'}
                </td>
                <td>{run.started_at ? new Date(run.started_at).toLocaleString() : '—'}</td>
                <td style={{ display: 'flex', gap: '5px' }}>
                  <button className="ad-btn ad-btn-sm" onClick={() => fetchCandidates(run.id)}>
                    View
                  </button>
                  <button
                    className="ad-btn ad-btn-sm ad-btn-danger"
                    onClick={() => deleteRun(run.id)}
                    title="Permanently delete this run and all its candidates/events"
                  >
                    Delete
                  </button>
                </td>
              </tr>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan="8" className="ad-empty">No discovery runs yet</td></tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      {/* DAS-2458: cross-run sortable table. Renders when viewMode === 'all'. */}
      {viewMode === 'all' && (
        <div className="ad-candidates">
          <div className="ad-candidates-header">
            <h3>
              All candidates — across runs
              <span style={{ fontSize: '0.75em', color: '#888', fontWeight: 'normal', marginLeft: 10 }}>
                ({allCandidates.length})
              </span>
            </h3>
            <div className="ad-candidates-actions">
              <span style={{ fontSize: '0.85em', color: '#666' }}>
                sorted by <code>{sortKey}</code> {sortDir === 'asc' ? '↑' : '↓'} · click any column header
              </span>
            </div>
          </div>
          {loadingAll ? (
            <div className="ad-loading">Loading all candidates…</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="ad-table ad-candidates-table">
                <thead>
                  <tr>
                    {[
                      ['id', 'ID'],
                      ['run_id', 'Run'],
                      ['alert_type', 'Type'],
                      ['alert_subtype', 'Subtype'],
                      ['status', 'Status'],
                      ['is_refined', 'Refined'],
                      ['regime_verdict', 'Verdict'],
                      ['composite_score', 'Score'],
                      ['total_fires', 'Fires'],
                      ['fires_per_year', 'Fires/yr'],
                      ['hit_rate_1m', 'Hit 1m'],
                      ['avg_return_1m', 'AvgRet 1m'],
                      ['hit_rate_alpha_sp500_1m', 'Hit α 1m'],
                      ['hit_rate_alpha_sp500_6m', 'Hit α 6m'],
                      ['hit_rate_alpha_sp500_1y', 'Hit α 1y'],
                      ['avg_alpha_sp500_1y', 'α 1y'],
                      ['unique_tickers', '#TKR'],
                      ['ticker_concentration', 'TConc'],
                      ['coverage_1y', 'Cov1y'],
                      ['promoted_alert_id', 'Live#'],
                    ].map(([key, label]) => (
                      <th
                        key={key}
                        onClick={() => requestSort(key)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={`Click to sort by ${label}`}
                      >
                        {label}{sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAllCandidates.map(c => {
                    const fmt = (v, digits = 2) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(digits);
                    const fmtPct = (v) => (v == null || isNaN(v)) ? '—' : `${Number(v).toFixed(1)}%`;
                    const expanded = expandedAllIds.has(c.id);
                    const canRefine = c.total_fires > 0;
                    const canAmend = !!c.is_refined;
                    return (
                      <React.Fragment key={c.id}>
                        <tr
                          style={{
                            cursor: 'pointer',
                            // DAS-2458: theme-neutral subtle highlight (works
                            // in both dark and light mode — no hard-coded color).
                            background: expanded ? 'rgba(120, 170, 255, 0.08)' : undefined,
                          }}
                          onClick={() => toggleAllRowExpansion(c.id)}
                          title="Click to expand details + actions"
                        >
                          <td>
                            <span style={{ color: '#888', marginRight: 4 }}>
                              {expanded ? '▼' : '▶'}
                            </span>
                            <strong>#{c.id}</strong>
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <button
                              className="ad-btn ad-btn-sm"
                              onClick={() => { setViewMode('run'); fetchCandidates(c.run_id); }}
                              title={`Switch to per-run view of run #${c.run_id}`}
                            >
                              #{c.run_id}
                            </button>
                          </td>
                          <td>{c.alert_type}</td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={c.alert_subtype}>
                            {c.alert_subtype || '—'}
                          </td>
                          <td>{c.status}</td>
                          <td>{c.is_refined ? '✓' : ''}</td>
                          <td style={{ fontSize: '0.85em' }}>{c.regime_verdict || '—'}</td>
                          <td><strong>{fmt(c.composite_score, 1)}</strong></td>
                          <td>{c.total_fires ?? '—'}</td>
                          <td>{fmt(c.fires_per_year, 0)}</td>
                          <td>{fmtPct(c.hit_rate_1m)}</td>
                          <td>{fmt(c.avg_return_1m, 2)}</td>
                          <td>{fmtPct(c.hit_rate_alpha_sp500_1m)}</td>
                          <td>{fmtPct(c.hit_rate_alpha_sp500_6m)}</td>
                          <td>{fmtPct(c.hit_rate_alpha_sp500_1y)}</td>
                          <td>{fmt(c.avg_alpha_sp500_1y, 2)}</td>
                          <td>{c.unique_tickers ?? '—'}</td>
                          <td>{fmt(c.ticker_concentration, 4)}</td>
                          <td>{fmt(c.coverage_1y, 2)}</td>
                          <td>{c.promoted_alert_id || '—'}</td>
                          <td onClick={e => e.stopPropagation()}>
                            <button
                              className="ad-btn ad-btn-sm"
                              onClick={() => { setViewMode('run'); fetchCandidates(c.run_id); setCandidateIdSearch(String(c.id)); setExpandedCandidate(c.id); loadBacktestEvents(c.id); }}
                              title="Open in per-run view with the full action set (slice by regime/sector, etc.)"
                            >
                              Open ▸
                            </button>
                          </td>
                        </tr>
                        {expanded && (
                          // DAS-2458: theme-neutral expansion panel — relies on
                          // inherited text color and uses semi-transparent bg
                          // so it works in both dark and light modes.
                          <tr style={{ background: 'rgba(120, 170, 255, 0.05)' }}>
                            <td colSpan={21} style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16 }}>
                                <div>
                                  <div style={{ marginBottom: 8, lineHeight: 1.7 }}>
                                    <strong>Clauses:</strong>{' '}
                                    {Array.isArray(c.clauses_json) && c.clauses_json.length
                                      ? c.clauses_json.map(id =>
                                          <code key={id} style={{
                                            margin: '0 4px',
                                            padding: '2px 6px',
                                            background: 'rgba(120, 170, 255, 0.18)',
                                            border: '1px solid rgba(120, 170, 255, 0.35)',
                                            borderRadius: 3,
                                            color: 'inherit',
                                          }}>
                                            #{id}{clauseTexts[id] ? ` ${clauseTexts[id].slice(0, 40)}…` : ''}
                                          </code>)
                                      : '—'}
                                  </div>
                                  {c.rationale && (
                                    <div style={{ marginBottom: 6 }}>
                                      <strong>Rationale:</strong> <span style={{ fontStyle: 'italic', opacity: 0.9 }}>{c.rationale}</span>
                                    </div>
                                  )}
                                  {c.advice_txt && (
                                    <div style={{ marginBottom: 6, opacity: 0.9 }}>
                                      <strong>Advice:</strong> {c.advice_txt}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.85em', opacity: 0.65 }}>
                                    archetype: {c.generation_archetype || '—'} ·
                                    sampling: {c.sample_frequency_days || '—'}d ·
                                    effective start: {c.effective_backtest_start || '—'} ·
                                    {c.amended_from_candidate_id && ` ↳ amended from #${c.amended_from_candidate_id} (${c.amendment_archetype || 'unknown'})`}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
                                  {canRefine && (
                                    <button
                                      className="ad-btn ad-btn-primary ad-btn-sm"
                                      onClick={() => refineCandidate(c.id)}
                                      disabled={refiningId === c.id}
                                      title={c.is_refined
                                        ? `Already refined at ${c.sample_frequency_days}d. Click to re-refine.`
                                        : 'Re-run backtest at 7-day granularity'}
                                    >
                                      {refiningId === c.id ? 'Refining…' : (c.is_refined ? 'Re-refine (7-day)' : 'Refine (7-day)')}
                                    </button>
                                  )}
                                  {canAmend && (
                                    <button
                                      className="ad-btn ad-btn-sm"
                                      onClick={() => openAmendModal(c.id)}
                                      title="Ask the LLM to propose 3 clause-tuning variants. ~$0.05."
                                    >
                                      Propose amendments
                                    </button>
                                  )}
                                  <button
                                    className="ad-btn ad-btn-sm"
                                    onClick={() => { setViewMode('run'); fetchCandidates(c.run_id); setCandidateIdSearch(String(c.id)); setExpandedCandidate(c.id); loadBacktestEvents(c.id); }}
                                    title="Switch to the per-run view to see backtest events, regime/sector slices, etc."
                                  >
                                    Open in per-run view ▸
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {!sortedAllCandidates.length && (
                    <tr><td colSpan="21" className="ad-empty">No candidates yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === 'run' && selectedRunId && (
        <div className="ad-candidates">
          <div className="ad-candidates-header">
            <h3>Candidates — Run #{selectedRunId}</h3>
            <div className="ad-candidates-actions">
              {/* DAS-2458: search-by-ID box. Substring match on candidate.id. */}
              <input
                type="text"
                value={candidateIdSearch}
                onChange={e => setCandidateIdSearch(e.target.value.trim())}
                placeholder="Find ID…"
                title="Filter the table to candidates whose ID contains this string. Example: 960 → #960. Clear to show all."
                style={{
                  padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc',
                  width: 90, fontVariantNumeric: 'tabular-nums',
                }}
              />
              {candidateIdSearch && (
                <button
                  className="ad-btn ad-btn-sm"
                  onClick={() => setCandidateIdSearch('')}
                  title="Clear ID filter"
                >Clear</button>
              )}
              <button className="ad-btn ad-btn-sm" onClick={selectAll}>Select All Proposed</button>
              <button
                className="ad-btn ad-btn-primary"
                onClick={runBacktest}
                disabled={backtesting || selectedCandidates.size === 0}
              >
                {backtesting ? 'Backtesting...' : `Backtest Selected (${selectedCandidates.size})`}
              </button>
              <button
                className="ad-btn ad-btn-sm"
                onClick={refineAll}
                disabled={refineAllProgress !== null || refineAllEligible().length === 0}
                title="Refine (7-day sampling) every backtested candidate that fired but isn't yet refined. Serial — takes ~2 min per candidate."
              >
                {refineAllProgress
                  ? `Refining ${refineAllProgress.done}/${refineAllProgress.total}...`
                  : `Refine All (${refineAllEligible().length})`}
              </button>
              {/* DAS-2430 */}
              <button
                className="ad-btn ad-btn-sm"
                onClick={amendAllEligibleInRun}
                disabled={amendingRun}
                title="DAS-2430: ask the LLM to propose 3 clause-tuning variants for every eligible (refined + ≥30 fires + ≥10 tickers + coverage_1y ≥ 0.30) candidate in this run. ~$0.05/cand."
              >
                {amendingRun ? 'Amending…' : 'Amend all eligible'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ad-loading">Loading candidates...</div>
          ) : (
            <table className="ad-table ad-candidates-table">
              <thead>
                <tr>
                  <th className="col-check"></th>
                  <th title="Internal candidate ID">ID</th>
                  <th title="Alert direction: Accumulate (buy / expect up), Harvest (sell / expect down), Step In (deep value / crisis buy), Monitor (info only)">Type</th>
                  <th title="IDs of clauses combined in this candidate (AND-logic)">Clauses</th>
                  <th title="Lifecycle: proposed → backtested → promoted">Status</th>
                  <th title="Composite score 0-100. Base: 30% hit rate 1m + 30% avg return 1m + 20% fires/yr + 20% consistency. Halved if avg α vs S&P 1y is negative (long-horizon penalty, DAS-2383). Hover the value for the full per-candidate breakdown.">Score</th>
                  <th title="% of fires where the direction-adjusted 1-month return was positive. Accumulate: % where stock went UP. Harvest: % where stock went DOWN (sign flipped). Measures directional accuracy, NOT vs benchmark.">Hit Rate 1m</th>
                  <th title="Average direction-adjusted 1-month return across all fires (%). Positive = alert direction was correct on average.">Avg Return 1m</th>
                  <th title="Average alpha vs S&P 500 over 1 month (%). = (ticker return − S&P 500 return) averaged across all fires. Positive = candidate beats market.">α S&P 1m</th>
                  <th title="Average alpha vs MSCI World (EUR) over 1 month (%). = (ticker return − MSCI return) averaged across all fires.">α MSCI 1m</th>
                  <th title="Average alpha vs S&P 500 over 1 year (%). Long-horizon excess return per fire.">α S&P 1y</th>
                  <th title="% of fires where 1-month ticker return BEAT S&P 500. >50% means consistently beats the market over 1m.">Hit α S&P 1m</th>
                  <th title="% of fires where 6-month ticker return BEAT S&P 500. >50% means consistently beats the market over 6m.">Hit α S&P 6m</th>
                  <th title="% of fires where 1-year ticker return BEAT S&P 500. >50% means consistently beats the market over 1y.">Hit α S&P 1y</th>
                  <th title="% of fires where 1-month ticker return BEAT MSCI World.">Hit α MSCI 1m</th>
                  <th title="% of fires where 6-month ticker return BEAT MSCI World.">Hit α MSCI 6m</th>
                  <th title="% of fires where 1-year ticker return BEAT MSCI World.">Hit α MSCI 1y</th>
                  <th title="Average number of times this candidate would have fired per year (normalised across backtest period)">Fires/yr</th>
                  <th title="DAS-2422: number of distinct tickers that fired. < 5 = the 'pattern' is a one-or-two-stock story, not a market regularity. The propose-gate refuses to promote those (DAS-2414).">#TKR</th>
                  <th title="DAS-2422: ticker concentration (Herfindahl Σ pᵢ²). 1.0 = single ticker dominates. > 0.6 = 1-2 tickers do most of the firing.">TConc</th>
                  <th title="LLM's investment thesis for why this combination should work">Rationale</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {/* DAS-2458: ID-substring filter (case-insensitive). */}
                {candidates
                  .filter(c => !candidateIdSearch || String(c.id).includes(candidateIdSearch))
                  .map(c => (
                  <React.Fragment key={c.id}>
                    <tr className={selectedCandidates.has(c.id) ? 'selected' : ''}>
                      <td className="col-check">
                        {c.status === 'proposed' && (
                          <input
                            type="checkbox"
                            checked={selectedCandidates.has(c.id)}
                            onChange={() => toggleCandidate(c.id)}
                          />
                        )}
                      </td>
                      <td>
                        {c.id}
                        {/* DAS-2430 lineage badge */}
                        {c.amended_from_candidate_id && (
                          <Tooltip
                            title={`DAS-2430: this candidate is an amendment of #${c.amended_from_candidate_id} (${c.amendment_archetype || 'variant'}).`}
                            arrow
                          >
                            <span style={{
                              display: 'inline-block',
                              marginLeft: 4,
                              padding: '1px 5px',
                              fontSize: '0.65em',
                              borderRadius: 3,
                              background: 'rgba(120, 80, 220, 0.18)',
                              color: '#a98bff',
                              fontWeight: 600,
                              verticalAlign: 'middle',
                            }}>
                              ↳#{c.amended_from_candidate_id}
                            </span>
                          </Tooltip>
                        )}
                      </td>
                      <td>
                        <span className={`alert-type at-${c.alert_type?.toLowerCase()}`}>{c.alert_type}</span>
                        {c.generation_archetype && (
                          <Tooltip
                            title={`DAS-2431: this candidate was generated under the "${c.generation_archetype}" archetype constraint.`}
                            arrow
                          >
                            <span style={{
                              display: 'inline-block',
                              marginLeft: 6,
                              padding: '2px 6px',
                              fontSize: '0.65em',
                              borderRadius: 4,
                              background: '#1a3a5a',
                              color: '#9fc4eb',
                              cursor: 'help',
                              fontFamily: 'monospace',
                            }}>
                              {c.generation_archetype.replace(/_/g, ' ').slice(0, 14)}
                            </span>
                          </Tooltip>
                        )}
                      </td>
                      <td className="clause-ids">
                        <Tooltip
                          arrow
                          placement="right"
                          title={
                            <div style={{ fontSize: '0.85rem', lineHeight: 1.4, maxWidth: 480 }}>
                              {(c.clauses_json || []).map(id => (
                                <div key={id} style={{ marginBottom: 4 }}>
                                  <strong>[{id}]</strong> {clauseTexts[id] || '(no text loaded)'}
                                </div>
                              ))}
                            </div>
                          }
                        >
                          <span style={{ cursor: 'help' }}>
                            {(c.clauses_json || []).join(', ')}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{getStatusBadge(c.status)}{renderRegimeVerdict(c)}</td>
                      <td className={getScoreColor(c.composite_score)}>
                        {c.composite_score != null ? (
                          <Tooltip arrow placement="right" title={renderScoreTooltip(c)}>
                            <span style={{ cursor: 'help' }}>{c.composite_score.toFixed(1)}</span>
                          </Tooltip>
                        ) : '—'}
                      </td>
                      <td>{formatPct(c.hit_rate_1m)}</td>
                      <td>{formatPct(c.avg_return_1m)}</td>
                      <td>{renderAlphaCell(c, 'sp500_1m')}</td>
                      <td>{renderAlphaCell(c, 'msci_1m')}</td>
                      <td>{renderAlphaCell(c, 'sp500_1y')}</td>
                      <td>{formatPct(c.hit_rate_alpha_sp500_1m)}</td>
                      <td>{formatPct(c.hit_rate_alpha_sp500_6m)}</td>
                      <td>{formatPct(c.hit_rate_alpha_sp500_1y)}</td>
                      <td>{formatPct(c.hit_rate_alpha_msci_1m)}</td>
                      <td>{formatPct(c.hit_rate_alpha_msci_6m)}</td>
                      <td>{formatPct(c.hit_rate_alpha_msci_1y)}</td>
                      <td>{renderFiresPerYearCell(c)}</td>
                      <td style={{
                        color: c.unique_tickers != null && Number(c.unique_tickers) < 5 ? '#e74c3c' : undefined,
                        fontWeight: c.unique_tickers != null && Number(c.unique_tickers) < 5 ? 'bold' : undefined,
                      }} title={c.unique_tickers != null && Number(c.unique_tickers) < 5 ? 'Below the propose-gate of 5 (DAS-2414/2422). This candidate would be skipped by the autonomous loop.' : ''}>
                        {c.unique_tickers ?? '—'}
                      </td>
                      <td style={{
                        color: c.ticker_concentration != null && Number(c.ticker_concentration) > 0.6 ? '#e74c3c' : undefined,
                      }}>
                        {c.ticker_concentration != null ? Number(c.ticker_concentration).toFixed(3) : '—'}
                      </td>
                      <td className="rationale-cell" title={c.rationale}>
                        {c.rationale?.substring(0, 80)}{c.rationale?.length > 80 ? '...' : ''}
                      </td>
                      <td className="action-cell">
                        {c.status === 'backtested' && (
                          <>
                            {/* DAS-2415: when the auto-classifier recommends a regime,
                                offer a primary "Promote <REGIME>-gated" button INSTEAD
                                of the bare Approve. User still confirms. */}
                            {c.regime_recommendation && canApprove(c) ? (
                              <button
                                className="ad-btn ad-btn-sm ad-btn-success"
                                onClick={() => promoteWithRegime(c, c.regime_recommendation)}
                                title={`Auto-classifier recommends ${c.regime_recommendation} gating (verdict: ${c.regime_verdict})`}
                              >
                                Promote {c.regime_recommendation}-gated
                              </button>
                            ) : (
                              <button
                                className="ad-btn ad-btn-sm ad-btn-success"
                                onClick={() => promoteCandidate(c)}
                                disabled={!canApprove(c)}
                                title={
                                  !c.total_fires || c.total_fires === 0
                                    ? 'Disabled: candidate has 0 fires in backtest'
                                    : !c.is_refined
                                    ? 'Disabled: must Refine (fine-grained backtest) first'
                                    : 'Promote to a live alert in Market_Alert_Definitions'
                                }
                              >
                                Approve
                              </button>
                            )}
                            {(c.total_fires > 0) && (
                              <button
                                className="ad-btn ad-btn-sm"
                                onClick={() => refineCandidate(c.id)}
                                disabled={refiningId === c.id}
                                title={
                                  c.is_refined
                                    ? `Already refined (last run: ${c.sample_frequency_days}d). Click to re-refine.`
                                    : 'Re-run backtest at 7-day granularity for validation'
                                }
                              >
                                {refiningId === c.id ? 'Refining…' : (c.is_refined ? 'Re-refine' : 'Refine')}
                              </button>
                            )}
                            {/* DAS-2430: per-candidate amendment button. Only
                                shows on refined candidates so we don't spam
                                the UI with disabled buttons. */}
                            {c.is_refined && (
                              <button
                                className="ad-btn ad-btn-sm"
                                onClick={() => openAmendModal(c.id)}
                                title="DAS-2430: ask the LLM to propose 3 clause-tuning variants of this candidate. ~$0.05 per call."
                              >
                                Propose amendments
                              </button>
                            )}
                            <button
                              className="ad-btn ad-btn-sm"
                              onClick={() => loadBacktestEvents(c.id)}
                            >
                              {expandedCandidate === c.id ? 'Hide' : 'Details'}
                            </button>
                            {(c.total_fires > 0) && (
                              <>
                                <button
                                  className="ad-btn ad-btn-sm"
                                  onClick={() => sliceByRegime(c.id)}
                                  disabled={slicingId === c.id}
                                  title="DAS-2413: split this candidate's fires by market regime (BULL/BEAR/NEUTRAL). If one regime dominates the signal, promote with that regime as a gating clause."
                                >
                                  {slicingId === c.id
                                    ? 'Slicing…'
                                    : (expandedRegimeCandidate === c.id ? 'Hide regimes' : 'Slice by regime')}
                                </button>
                                <button
                                  className="ad-btn ad-btn-sm"
                                  onClick={() => sliceBySector(c.id)}
                                  disabled={sectorSlicingId === c.id}
                                  title="DAS-2416: split this candidate's fires by ticker sector. If one sector carries the signal, promote with a sector-gating clause."
                                >
                                  {sectorSlicingId === c.id
                                    ? 'Slicing…'
                                    : (expandedSectorCandidate === c.id ? 'Hide sectors' : 'Slice by sector')}
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {c.status === 'promoted' && (
                          <span className="promoted-label">Alert #{c.promoted_alert_id}</span>
                        )}
                      </td>
                    </tr>
                    {expandedRegimeCandidate === c.id && regimeSlices[c.id] && (
                      <tr className="expanded-row">
                        <td colSpan="20">
                          <div className="backtest-events-detail">
                            <h4>Regime slice (DAS-2413)</h4>
                            {!c.is_refined && (
                              <div style={{
                                background: 'rgba(230, 167, 0, 0.15)',
                                border: '1px solid #e6a700',
                                borderRadius: 4,
                                padding: '6px 10px',
                                marginBottom: 8,
                                fontSize: '0.85rem',
                                color: '#e6a700',
                              }}>
                                ⚠ Based on 30-day sampling (unrefined). Auto-classifier uses
                                stricter thresholds. Refine the candidate for tighter numbers.
                              </div>
                            )}
                            <p style={{ fontSize: '0.85rem', color: '#aaa', marginTop: 0 }}>
                              The same fires partitioned by the market regime
                              (VIX + S&P 200-day MA hybrid) active on each fire date.
                              If one regime materially outperforms <strong>ALL</strong>,
                              you can promote with that regime as a gating clause.
                            </p>
                            <table className="ad-table events-table">
                              <thead>
                                <tr>
                                  <th>Regime</th>
                                  <th>Fires</th>
                                  <th>Fires/yr</th>
                                  <th>Score</th>
                                  <th>Hit 1m</th>
                                  <th>Avg ret 1m</th>
                                  <th>α S&P 1y</th>
                                  <th>Med α S&P 1y</th>
                                  <th>Hit α S&P 1y</th>
                                  <th>tc</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {regimeSlices[c.id].map((s, i) => {
                                  const isBaseline = s.regime === 'ALL';
                                  const promoteKey = `${c.id}:${s.regime}`;
                                  return (
                                    <tr key={i} style={isBaseline ? { fontStyle: 'italic', color: '#aaa' } : {}}>
                                      <td><strong>{s.regime}</strong>{isBaseline ? ' (baseline)' : ''}</td>
                                      <td>{s.total_fires ?? 0}</td>
                                      <td>{s.fires_per_year != null ? Number(s.fires_per_year).toFixed(1) : '—'}</td>
                                      <td className={getScoreColor(s.composite_score)}>
                                        {s.composite_score != null ? Number(s.composite_score).toFixed(1) : '—'}
                                      </td>
                                      <td>{formatPct(s.hit_rate_1m != null ? Number(s.hit_rate_1m) : null)}</td>
                                      <td>{formatPct(s.avg_return_1m != null ? Number(s.avg_return_1m) : null)}</td>
                                      <td>{formatPct(s.avg_alpha_sp500_1y != null ? Number(s.avg_alpha_sp500_1y) : null)}</td>
                                      <td>{formatPct(s.median_alpha_sp500_1y != null ? Number(s.median_alpha_sp500_1y) : null)}</td>
                                      <td>{formatPct(s.hit_rate_alpha_sp500_1y != null ? Number(s.hit_rate_alpha_sp500_1y) : null)}</td>
                                      <td>{s.temporal_concentration != null
                                          ? `${(Number(s.temporal_concentration) * 100).toFixed(0)}%`
                                          : '—'}</td>
                                      <td>
                                        {!isBaseline && (s.total_fires ?? 0) >= 3 && canApprove(c) && (
                                          <button
                                            className="ad-btn ad-btn-sm ad-btn-success"
                                            onClick={() => promoteWithRegime(c, s.regime)}
                                            disabled={promotingRegimeId === promoteKey}
                                            title={`Promote candidate with ${s.regime} regime as a gating clause`}
                                          >
                                            {promotingRegimeId === promoteKey
                                              ? 'Promoting…'
                                              : `Promote ${s.regime}-gated`}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedSectorCandidate === c.id && sectorSlices[c.id] && (
                      <tr className="expanded-row">
                        <td colSpan="20">
                          <div className="backtest-events-detail">
                            <h4>Sector slice (DAS-2416)</h4>
                            {!c.is_refined && (
                              <div style={{
                                background: 'rgba(230, 167, 0, 0.15)',
                                border: '1px solid #e6a700',
                                borderRadius: 4,
                                padding: '6px 10px',
                                marginBottom: 8,
                                fontSize: '0.85rem',
                                color: '#e6a700',
                              }}>
                                ⚠ Based on 30-day sampling (unrefined). Refine for tighter numbers.
                              </div>
                            )}
                            <p style={{ fontSize: '0.85rem', color: '#aaa', marginTop: 0 }}>
                              The same fires partitioned by the fired ticker's GICS sector.
                              Sectors with &lt; 5 fires bucket into <strong>Other</strong>.
                              If one sector outperforms <strong>ALL</strong>,
                              promote with a sector-gating clause.
                            </p>
                            <table className="ad-table events-table">
                              <thead>
                                <tr>
                                  <th>Sector</th>
                                  <th>Fires</th>
                                  <th>Fires/yr</th>
                                  <th>Score</th>
                                  <th>Hit 1m</th>
                                  <th>Avg ret 1m</th>
                                  <th>α S&P 1y</th>
                                  <th>Med α S&P 1y</th>
                                  <th>Hit α S&P 1y</th>
                                  <th>tc</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {sectorSlices[c.id].map((s, i) => {
                                  const isBaseline = s.sector === 'ALL';
                                  const isOther = s.sector === 'Other';
                                  const promoteKey = `${c.id}:${s.sector}`;
                                  return (
                                    <tr key={i} style={isBaseline ? { fontStyle: 'italic', color: '#aaa' } : {}}>
                                      <td><strong>{s.sector}</strong>{isBaseline ? ' (baseline)' : ''}</td>
                                      <td>{s.total_fires ?? 0}</td>
                                      <td>{s.fires_per_year != null ? Number(s.fires_per_year).toFixed(1) : '—'}</td>
                                      <td className={getScoreColor(s.composite_score)}>
                                        {s.composite_score != null ? Number(s.composite_score).toFixed(1) : '—'}
                                      </td>
                                      <td>{formatPct(s.hit_rate_1m != null ? Number(s.hit_rate_1m) : null)}</td>
                                      <td>{formatPct(s.avg_return_1m != null ? Number(s.avg_return_1m) : null)}</td>
                                      <td>{formatPct(s.avg_alpha_sp500_1y != null ? Number(s.avg_alpha_sp500_1y) : null)}</td>
                                      <td>{formatPct(s.median_alpha_sp500_1y != null ? Number(s.median_alpha_sp500_1y) : null)}</td>
                                      <td>{formatPct(s.hit_rate_alpha_sp500_1y != null ? Number(s.hit_rate_alpha_sp500_1y) : null)}</td>
                                      <td>{s.temporal_concentration != null
                                          ? `${(Number(s.temporal_concentration) * 100).toFixed(0)}%`
                                          : '—'}</td>
                                      <td>
                                        {!isBaseline && !isOther && (s.total_fires ?? 0) >= 10 && canApprove(c) && (
                                          <button
                                            className="ad-btn ad-btn-sm ad-btn-success"
                                            onClick={() => promoteWithSector(c, s.sector)}
                                            disabled={promotingSectorId === promoteKey}
                                            title={`Promote candidate with sector="${s.sector}" as a gating clause`}
                                          >
                                            {promotingSectorId === promoteKey
                                              ? 'Promoting…'
                                              : `Promote ${s.sector}-gated`}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedCandidate === c.id && backtestEvents[c.id] && (
                      <tr className="expanded-row">
                        <td colSpan="20">
                          <div className="backtest-events-detail">
                            <h4>Backtest Events ({backtestEvents[c.id].length} fires)</h4>
                            <div className="events-scroll">
                              <table className="ad-table events-table">
                                <thead>
                                  <tr>
                                    <th title="Stock ticker">Ticker</th>
                                    <th title="Date the alert would have fired">Fire Date</th>
                                    <th title="Ticker close price on fire date">Price</th>
                                    <th title="Direction-adjusted 1-week ticker return">1w Ret</th>
                                    <th title="Direction-adjusted 1-month ticker return">1m Ret</th>
                                    <th title="Direction-adjusted 6-month ticker return">6m Ret</th>
                                    <th title="Direction-adjusted 1-year ticker return">1y Ret</th>
                                    <th title="S&P 500 1-month return for the same fire date (benchmark)">S&P 1m</th>
                                    <th title="S&P 500 1-year return for the same fire date (benchmark)">S&P 1y</th>
                                    <th title="Alpha = ticker 1m return − S&P 500 1m return">α S&P 1m</th>
                                    <th title="Alpha = ticker 1m return − MSCI World 1m return">α MSCI 1m</th>
                                    <th title="Alpha = ticker 1y return − S&P 500 1y return">α S&P 1y</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {backtestEvents[c.id].slice(0, 50).map((ev, idx) => (
                                    <tr key={idx}>
                                      <td>{ev.ticker}</td>
                                      <td>{ev.fire_date}</td>
                                      <td>{ev.price_at_fire?.toFixed(2)}</td>
                                      <td>{formatPct(ev.AE_1w_absolute_return)}</td>
                                      <td>{formatPct(ev.AE_1m_absolute_return)}</td>
                                      <td>{formatPct(ev.AE_6m_absolute_return)}</td>
                                      <td>{formatPct(ev.AE_1y_absolute_return)}</td>
                                      <td>{formatPct(ev.sp500_return_1m)}</td>
                                      <td>{formatPct(ev.sp500_return_1y)}</td>
                                      <td>{formatPct(ev.alpha_sp500_1m)}</td>
                                      <td>{formatPct(ev.alpha_msci_1m)}</td>
                                      <td>{formatPct(ev.alpha_sp500_1y)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {backtestEvents[c.id].length > 50 && (
                                <div className="events-truncated">
                                  Showing 50 of {backtestEvents[c.id].length} events
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {/* DAS-2458: "no match" row when the ID filter excludes everything. */}
                {candidates.length > 0 && candidateIdSearch &&
                 candidates.filter(c => String(c.id).includes(candidateIdSearch)).length === 0 && (
                  <tr><td colSpan="11" className="ad-empty">
                    No candidate ID matches "{candidateIdSearch}" in this run
                  </td></tr>
                )}
                {candidates.length === 0 && (
                  <tr><td colSpan="11" className="ad-empty">No candidates for this run</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default AlertDiscovery;
